import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import { jsPDF } from "jspdf";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { FaEdit, FaTrash } from "react-icons/fa";
import {
  Barcode,
  Bluetooth,
  Camera,
  CheckCircle2,
  Clock3,
  Eye,
  History,
  Menu,
  UserRound,
  Monitor,
  Plus,
  Printer,
  Search,
  ScanLine,
  ShieldCheck,
  Trash2,
  Wrench,
  X,
} from "lucide-react";
import { API_BASE_URL } from "../../config/api";
import Sidebar from "../../components/Sidebar";
import {
  connectPhomemoM220,
  getPhomemoM220DeviceInfo,
  isWebBluetoothSupported,
  printServiceBarcodeM220,
} from "../../utils/phomemoM220";

// Phomemo M220: printim direkt 50 × 30 mm kur printeri është i lidhur.

/* =========================================================    OPTIONS ========================================================= */

const STATUS_OPTIONS = [
  { value: "pending", label: "Në pritje" },
  { value: "diagnosis", label: "Në diagnostikim" },
  { value: "repairing", label: "Në riparim" },
  { value: "ready", label: "Gati" },
  { value: "delivered", label: "Dorëzuar" },
  { value: "cancelled", label: "Anuluar" },
];

const WARRANTY_OPTIONS = [
  { value: "0", label: "Pa garanci" },
  { value: "1", label: "1 muaj" },
  { value: "3", label: "3 muaj" },
  { value: "6", label: "6 muaj" },
  { value: "12", label: "12 muaj" },
  { value: "24", label: "24 muaj" },
];

const EMPTY_ITEM = { article: "", quantity: 1, unitPrice: "" };

const EMPTY_FORM = {
  id: null,
  serviceCode: "",
  name: "",
  surname: "",
  phone: "",
  email: "",
  phoneType: "",
  issueDescription: "",
  clientNote: "",
  technicianConclusion: "",
  statusNote: "",
  warrantyMonths: "0",
  repairCost: "",
  status: "pending",
  readyDate: "",
  pickupDate: "",
  invoiceItems: [{ ...EMPTY_ITEM }],
};

/* =========================================================    HELPERS ========================================================= */

const formatMoney = (value) =>
  new Intl.NumberFormat("sq-AL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDate = (dateValue) => {
  if (!dateValue) return "—";

  const date = new Date(dateValue);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const generateServiceCode = () => {
  const now = new Date();
  const datePart = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0"),
  ].join("");

  const randomPart = Math.random().toString(36).slice(2, 10).toUpperCase();
  return `SRV-${datePart}-${randomPart}`;
};

const getStatusLabel = (status) =>
  STATUS_OPTIONS.find((option) => option.value === status)?.label ||
  status ||
  "Në pritje";

const getStatusClasses = (status) => {
  switch (status) {
    case "diagnosis":
      return "bg-amber-100 text-amber-700";
    case "repairing":
      return "bg-blue-100 text-blue-700";
    case "ready":
      return "bg-green-100 text-green-700";
    case "delivered":
      return "bg-emerald-100 text-emerald-700";
    case "cancelled":
      return "bg-red-100 text-red-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

const getHistoryActionLabel = (item) => {
  const actionType = String(item?.action_type || "").toLowerCase();
  const newStatus = String(item?.new_status || "").toLowerCase();

  if (actionType === "created") return "Regjistroi servisin";
  if (newStatus === "diagnosis") return "Kaloi në diagnostikim";
  if (newStatus === "repairing") return "Filloi / vazhdoi riparimin";
  if (newStatus === "ready") return "E përfundoi dhe e bëri gati";
  if (newStatus === "delivered") return "Ia dorëzoi klientit";
  if (newStatus === "cancelled") return "Anuloi servisin";
  return "Përditësoi servisin";
};

const parseInvoiceItems = (value) => {
  if (Array.isArray(value)) {
    return value.length ? value : [{ ...EMPTY_ITEM }];
  }
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) && parsed.length
        ? parsed
        : [{ ...EMPTY_ITEM }];
    } catch {
      return [{ ...EMPTY_ITEM }];
    }
  }
  return [{ ...EMPTY_ITEM }];
};

const normalizeRepair = (repair) => ({
  ...repair,
  service_price: Number(repair.service_price || 0),
  repair_cost: Number(repair.repair_cost || 0),
  user_profit: Number(repair.user_profit || 0),
  warranty_months: Number(repair.warranty_months || 0),
  invoice_items: parseInvoiceItems(repair.invoice_items),
});

// QR standard, me kontrast maksimal dhe quiet-zone 4 module.
// Kjo është zgjedhja më e sigurt për skanim me kamerë dhe ZXing.


const renderQrToCanvas = async (canvas, value, width = 220) => {
  if (!canvas || !value) return;

  await QRCode.toCanvas(canvas, String(value), {
    errorCorrectionLevel: "H",
    margin: 4,
    width,
    color: {
      dark: "#000000",
      light: "#ffffff",
    },
  });
};

/* =========================================================    COMPONENT ========================================================= */

const AdminRepairsManager = () => {
  const navigate = useNavigate();
  const formBarcodeRef = useRef(null);
  const scannerHandledRef = useRef(false);

  // ZXing scanner refs
  const cameraVideoRef = useRef(null);
  const screenVideoRef = useRef(null);
  const cameraScannerControlsRef = useRef(null);
  const screenScannerControlsRef = useRef(null);
  const screenStreamRef = useRef(null);
  const [repairs, setRepairs] = useState([]);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [barcodePrintingId, setBarcodePrintingId] = useState(null);
  const [printerConnected, setPrinterConnected] = useState(false);
  const [printerConnecting, setPrinterConnecting] = useState(false);
  const [printerName, setPrinterName] = useState("Asnjë printer");
  const webBluetoothSupported = isWebBluetoothSupported();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedRepair, setSelectedRepair] = useState(null);
  const [mobileDetailRepair, setMobileDetailRepair] = useState(null);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [fieldErrors, setFieldErrors] = useState({});
  const [repairHistory, setRepairHistory] = useState({});
  const [historyLoadingId, setHistoryLoadingId] = useState(null);
  const [editingOriginalStatus, setEditingOriginalStatus] = useState(null);

  const [historyEditItem, setHistoryEditItem] = useState(null);
  const [historyEditNote, setHistoryEditNote] = useState("");
  const [historyEditConclusion, setHistoryEditConclusion] = useState("");
  const [historyEditSaving, setHistoryEditSaving] = useState(false);

  const currentUser = useMemo(() => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return null;
      const payload = token.split(".")[1];
      if (!payload) return null;
      const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
      const decoded = decodeURIComponent(
        window
          .atob(normalized)
          .split("")
          .map((char) => `%${("00" + char.charCodeAt(0).toString(16)).slice(-2)}`)
          .join(""),
      );
      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }, []);

  const isCurrentUserAdmin =
    String(currentUser?.role || "").toLowerCase() === "admin";

  // User normal: mund të ndryshojë statusin dhe të shtojë shënime të reja.
  // Nuk mund të modifikojë fushat bazë të servisit dhe nuk mund të redaktojë log-et ekzistuese.
  // Log-et e historikut redaktohen vetëm nga admin.
  const isRestrictedUserEdit =
    Boolean(formData.id) && !isCurrentUserAdmin;

  // Scanner state
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerMode, setScannerMode] = useState(null); // null | camera | screen
  const [scannerValue, setScannerValue] = useState("");
  const [scannerBusy, setScannerBusy] = useState(false);
  /* =====================================================      TOTAL   ===================================================== */ const invoiceTotal =
    useMemo(
      () =>
        formData.invoiceItems.reduce(
          (total, item) =>
            total + Number(item.quantity || 0) * Number(item.unitPrice || 0),
          0,
        ),
      [formData.invoiceItems],
    );

  // Fitimi i userit që e bën servisin Gati:
  // 50% e fitimit neto = (Totali - Kosto riparimi) * 50%.
  // Nuk lejojmë fitim negativ.
  const userProfit = useMemo(() => {
    const total = Number(invoiceTotal || 0);
    const repairCost = Number(formData.repairCost || 0);
    return Math.max(0, (total - repairCost) * 0.5);
  }, [invoiceTotal, formData.repairCost]);
/* =====================================================
       QR CODES
       - I njëjti service_code përdoret në preview, faturë dhe garanci.
       - QR standard me error correction H dhe quiet-zone 4 module.
    ===================================================== */
  useEffect(() => {
    if (!dialogOpen || !formData.serviceCode || !formBarcodeRef.current) {
      return;
    }

    let cancelled = false;

    const drawQr = async () => {
      try {
        if (!cancelled && formBarcodeRef.current) {
          await renderQrToCanvas(
            formBarcodeRef.current,
            formData.serviceCode,
            210,
          );
        }
      } catch (error) {
        console.error("FORM QR ERROR:", error);
      }
    };

    drawQr();

    return () => {
      cancelled = true;
    };
  }, [dialogOpen, formData.serviceCode]);
/* =====================================================
       ZXING QR CODE + BARCODE SCANNER
       - Camera scan: getUserMedia through decodeFromConstraints
       - Screen scan: getDisplayMedia + decodeFromStream
       - QR nga kamera/screen dhe scanner-at manual/Bluetooth/USB mund ta dërgojnë kodin si tekst
    ===================================================== */

  const stopCameraScanner = () => {
    try {
      cameraScannerControlsRef.current?.stop();
    } catch (error) {
      console.warn("CAMERA SCANNER STOP ERROR:", error);
    }

    cameraScannerControlsRef.current = null;

    const video = cameraVideoRef.current;
    const stream = video?.srcObject;

    if (stream?.getTracks) {
      stream.getTracks().forEach((track) => track.stop());
    }

    if (video) {
      video.srcObject = null;
    }
  };

  const stopScreenScanner = () => {
    try {
      screenScannerControlsRef.current?.stop();
    } catch (error) {
      console.warn("SCREEN SCANNER STOP ERROR:", error);
    }

    screenScannerControlsRef.current = null;

    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((track) => track.stop());
      screenStreamRef.current = null;
    }

    if (screenVideoRef.current) {
      screenVideoRef.current.srcObject = null;
    }
  };

  const stopAllScanners = () => {
    stopCameraScanner();
    stopScreenScanner();
    setScannerBusy(false);
    setScannerMode(null);
  };

  const closeScannerModal = () => {
    stopAllScanners();
    setScannerValue("");
    setScannerOpen(false);
  };

  const normalizeScannedCode = (value) =>
    String(value || "")
      .trim()
      .replace(/\s+/g, "")
      .toUpperCase();

  const extractScannedServiceCode = (rawValue) => {
    const raw = String(rawValue || "").trim();
    if (!raw) return "";

    const normalizedRaw = normalizeScannedCode(raw);

    const directMatch = normalizedRaw.match(/SRV-[A-Z0-9-]+/);
    if (directMatch?.[0]) {
      return directMatch[0];
    }

    try {
      const url = new URL(raw);
      const queryCode =
        url.searchParams.get("service_code") ||
        url.searchParams.get("serviceCode") ||
        url.searchParams.get("code");

      if (queryCode) {
        return normalizeScannedCode(queryCode);
      }

      const parts = url.pathname.split("/").filter(Boolean);
      const lastPart = parts[parts.length - 1];

      if (lastPart) {
        const pathMatch = normalizeScannedCode(lastPart).match(/SRV-[A-Z0-9-]+/);
        if (pathMatch?.[0]) return pathMatch[0];
      }
    } catch {}

    return normalizedRaw;
  };

  const openRepairFromBarcode = (barcodeValue) => {
    const code = extractScannedServiceCode(barcodeValue);

    if (!code) {
      setMessage({
        type: "error",
        text: "QR/Barcode është bosh.",
      });
      return false;
    }

    const repair = repairs.find(
      (item) =>
        normalizeScannedCode(item.service_code) === normalizeScannedCode(code),
    );

    if (!repair) {
      scannerHandledRef.current = false;
      setScannerValue(code);
      setMessage({
        type: "error",
        text: `Kodi ${code} u lexua, por nuk u gjet servis me këtë service_code.`,
      });
      return false;
    }

    scannerHandledRef.current = true;

    stopAllScanners();
    setScannerOpen(false);
    setScannerMode(null);
    setScannerBusy(false);
    setScannerValue("");

    const normalizedRepair = normalizeRepair(repair);
    setMobileDetailRepair(normalizedRepair);

    void fetchRepairHistory(normalizedRepair.id, true);

    setMessage({
      type: "success",
      text: `U identifikua servisi ${repair.service_code}.`,
    });

    return true;
  };

  const startCameraScanner = async () => {
    stopAllScanners();
    scannerHandledRef.current = false;

    if (!window.isSecureContext) {
      setMessage({
        type: "error",
        text: "Scanner-i me kamerë kërkon HTTPS.",
      });
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setMessage({
        type: "error",
        text: "Kamera nuk është e disponueshme në këtë browser.",
      });
      return;
    }

    setScannerOpen(true);
    setScannerMode("camera");
    setScannerBusy(true);
    setMessage({ type: "", text: "" });

    try {
      // Presim që <video> të montohet pasi hapet modal-i.
      await new Promise((resolve) => window.setTimeout(resolve, 350));

      const videoElement = cameraVideoRef.current;

      if (!videoElement) {
        throw new Error("Elementi video i kamerës nuk u gjet.");
      }

      videoElement.setAttribute("playsinline", "true");
      videoElement.setAttribute("autoplay", "true");
      videoElement.muted = true;

      const codeReader = new BrowserMultiFormatReader();
      let lastHandledCode = "";

      const controls = await codeReader.decodeFromConstraints(
        {
          audio: false,
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        },
        videoElement,
        async (result, error) => {
          if (result && !scannerHandledRef.current) {
            const rawValue = result.getText?.() || String(result || "");
            const code = extractScannedServiceCode(rawValue);

            if (!code) return;

            if (lastHandledCode === code) return;
            lastHandledCode = code;

            console.log("QR/BARCODE I LEXUAR:", code);

            const repair = repairs.find(
              (item) =>
                normalizeScannedCode(item.service_code) ===
                normalizeScannedCode(code),
            );

            if (!repair) {
              setScannerValue(code);
              setMessage({
                type: "error",
                text: `Kodi ${code} u lexua, por nuk u gjet servis me këtë service_code.`,
              });

              window.setTimeout(() => {
                lastHandledCode = "";
              }, 1200);

              return;
            }

            scannerHandledRef.current = true;

            try {
              controls?.stop();
            } catch {}

            cameraScannerControlsRef.current = null;

            const stream = videoElement.srcObject;
            if (stream?.getTracks) {
              stream.getTracks().forEach((track) => track.stop());
            }

            videoElement.srcObject = null;

            setScannerBusy(false);
            setScannerMode(null);
            setScannerOpen(false);
            setScannerValue("");

            const normalizedRepair = normalizeRepair(repair);
            setMobileDetailRepair(normalizedRepair);

            void fetchRepairHistory(normalizedRepair.id, true);

            setMessage({
              type: "success",
              text: `U identifikua servisi ${repair.service_code}.`,
            });
          }

          if (error?.name && error.name !== "NotFoundException") {
            console.debug(
              "ZXING CAMERA SCAN:",
              error.name,
              error.message || ""
            );
          }
        }
      );

      cameraScannerControlsRef.current = controls;
      setScannerBusy(false);

      // Autofocus kur pajisja/browser-i e mbështet.
      try {
        const stream = videoElement.srcObject;
        const track = stream?.getVideoTracks?.()[0];
        const capabilities = track?.getCapabilities?.() || {};
        const advanced = [];

        if (
          Array.isArray(capabilities.focusMode) &&
          capabilities.focusMode.includes("continuous")
        ) {
          advanced.push({ focusMode: "continuous" });
        }

        if (advanced.length) {
          await track.applyConstraints({ advanced });
        }
      } catch (constraintError) {
        console.debug("CAMERA AUTOFOCUS:", constraintError);
      }
    } catch (error) {
      console.error("CAMERA SCANNER ERROR:", error);

      stopCameraScanner();
      setScannerBusy(false);
      setScannerMode(null);

      let errorText =
        error?.message || "Kamera nuk mund të hapej për skanim.";

      if (
        error?.name === "NotAllowedError" ||
        error?.name === "SecurityError"
      ) {
        errorText =
          "Aksesi te kamera është i bllokuar. Te Site Settings vendos Camera → Allow.";
      } else if (
        error?.name === "NotFoundError" ||
        error?.name === "DevicesNotFoundError"
      ) {
        errorText = "Nuk u gjet asnjë kamerë në këtë pajisje.";
      } else if (
        error?.name === "NotReadableError" ||
        error?.name === "TrackStartError"
      ) {
        errorText =
          "Kamera është duke u përdorur nga një aplikacion tjetër.";
      }

      setMessage({
        type: "error",
        text: errorText,
      });
    }
  };

  const startScreenScanner = async () => {
    stopAllScanners();
    scannerHandledRef.current = false;

    if (!navigator.mediaDevices?.getDisplayMedia) {
      setMessage({
        type: "error",
        text: "Ky browser/pajisje nuk mbështet screen sharing. Në telefon përdor skanimin me kamerë.",
      });
      return;
    }

    setScannerOpen(true);
    setScannerMode("screen");
    setScannerBusy(true);
    setMessage({ type: "", text: "" });

    try {
      await new Promise((resolve) => window.setTimeout(resolve, 150));

      const videoElement = screenVideoRef.current;

      if (!videoElement) {
        throw new Error("Elementi video për screen scanning nuk u gjet.");
      }

      // getDisplayMedia duhet të thirret si rezultat direkt i klikimit të përdoruesit.
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
      });

      screenStreamRef.current = stream;

      const [videoTrack] = stream.getVideoTracks();
      videoTrack?.addEventListener("ended", () => {
        stopScreenScanner();
        setScannerBusy(false);
        setScannerMode(null);
      });

      const codeReader = new BrowserMultiFormatReader();

      const controls = await codeReader.decodeFromStream(
        stream,
        videoElement,
        (result, error, controlsInstance) => {
          if (result && !scannerHandledRef.current) {
            const value = result.getText?.() || String(result);
            scannerHandledRef.current = true;
            controlsInstance?.stop();
            openRepairFromBarcode(value);
            return;
          }

          if (error?.name && error.name !== "NotFoundException") {
            console.debug("ZXING SCREEN SCAN:", error.name);
          }
        },
      );

      screenScannerControlsRef.current = controls;
    } catch (error) {
      console.error("SCREEN SCANNER ERROR:", error);
      stopScreenScanner();
      setScannerBusy(false);
      setScannerMode(null);
      setMessage({
        type: "error",
        text:
          error?.name === "NotAllowedError"
            ? "Screen sharing u anulua ose nuk u lejua."
            : error?.message || "Screen scanning nuk mund të nisej.",
      });
    }
  };

  const handleManualBarcodeSubmit = (event) => {
    event.preventDefault();
    openRepairFromBarcode(scannerValue);
  };

  useEffect(() => {
    return () => {
      stopCameraScanner();
      stopScreenScanner();
    };
  }, []);

  /* =====================================================      FETCH   ===================================================== */ const fetchRepairs =
    useCallback(async () => {
      const token = localStorage.getItem("authToken");
      if (!token) {
        navigate("/login");
        return;
      }
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/repairs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const responseText = await response.text();
        let data = {};
        try {
          data = responseText ? JSON.parse(responseText) : {};
        } catch {
          data = { error: responseText };
        }
        if (!response.ok) {
          throw new Error(
            data?.details ||
              data?.sqlMessage ||
              data?.error ||
              data?.message ||
              "Serviset nuk mund të ngarkoheshin.",
          );
        }
        const rows = Array.isArray(data)
          ? data
          : data.repairs || data.data || [];
        setRepairs(rows.map(normalizeRepair));
      } catch (error) {
        console.error("FETCH REPAIRS ERROR:", error);
        setMessage({
          type: "error",
          text: error.message || "Serviset nuk mund të ngarkoheshin.",
        });
      } finally {
        setLoading(false);
      }
    }, [navigate]);
  useEffect(() => {
    fetchRepairs();
  }, [fetchRepairs]);
  /* =====================================================      FILTER   ===================================================== */

  const filteredRepairs = useMemo(() => {
    const searchText = search.trim().toLowerCase();
    return repairs.filter((repair) => {
      if (statusFilter !== "all" && repair.status !== statusFilter) {
        return false;
      }
      if (!searchText) {
        return true;
      }
      return [
        repair.service_code,
        repair.name,
        repair.surname,
        repair.phone,
        repair.email,
        repair.phone_type,
        repair.issue_description,
        repair.created_by_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(searchText);
    });
  }, [repairs, search, statusFilter]);
  const fetchRepairHistory = useCallback(
    async (repairId, force = false) => {
      if (!repairId) return [];
      if (!force && Array.isArray(repairHistory[repairId])) {
        return repairHistory[repairId];
      }

      const token = localStorage.getItem("authToken");
      if (!token) {
        navigate("/login");
        return [];
      }

      setHistoryLoadingId(Number(repairId));
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/repairs/${repairId}/history`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const data = await response.json().catch(() => []);
        if (!response.ok) {
          throw new Error(data?.error || "Historiku nuk mund të ngarkohej.");
        }
        const rows = Array.isArray(data) ? data : [];
        setRepairHistory((previous) => ({ ...previous, [repairId]: rows }));
        return rows;
      } catch (error) {
        setMessage({ type: "error", text: error.message });
        return [];
      } finally {
        setHistoryLoadingId(null);
      }
    },
    [navigate, repairHistory],
  );

  const openRepairDetails = async (repair) => {
    const normalized = normalizeRepair(repair);
    setMobileDetailRepair(normalized);
    await fetchRepairHistory(normalized.id);
  };

  const getReadyUserName = (repairId) => {
    const history = Array.isArray(repairHistory[repairId])
      ? repairHistory[repairId]
      : [];

    const readyEntries = history
      .filter(
        (item) =>
          String(item?.new_status || "").toLowerCase() === "ready"
      )
      .sort(
        (a, b) =>
          new Date(b?.created_at || 0).getTime() -
          new Date(a?.created_at || 0).getTime()
      );

    return readyEntries[0]?.user_name || "—";
  };

  const openHistoryEdit = (item) => {
    if (!isCurrentUserAdmin || !item || item.virtual) return;

    setHistoryEditItem(item);
    setHistoryEditNote(item.note || "");
    setHistoryEditConclusion(item.technician_conclusion || "");
  };

  const closeHistoryEdit = () => {
    if (historyEditSaving) return;

    setHistoryEditItem(null);
    setHistoryEditNote("");
    setHistoryEditConclusion("");
  };

  const saveHistoryEdit = async () => {
    if (
      !isCurrentUserAdmin ||
      !mobileDetailRepair?.id ||
      !historyEditItem?.id
    ) {
      return;
    }

    const token = localStorage.getItem("authToken");

    if (!token) {
      navigate("/login");
      return;
    }

    setHistoryEditSaving(true);
    setMessage({ type: "", text: "" });

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/repairs/${mobileDetailRepair.id}/history/${historyEditItem.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            note: historyEditNote.trim(),
            technicianConclusion: historyEditConclusion.trim(),
          }),
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.error ||
            data?.details ||
            "Historiku nuk mund të përditësohej.",
        );
      }

      const updatedEntry = data?.history_entry;

      if (updatedEntry) {
        setRepairHistory((previous) => ({
          ...previous,
          [mobileDetailRepair.id]: (
            previous[mobileDetailRepair.id] || []
          ).map((item) =>
            Number(item.id) === Number(updatedEntry.id)
              ? updatedEntry
              : item,
          ),
        }));
      }

      setMessage({
        type: "success",
        text: "Hyrja e historikut u përditësua nga administratori.",
      });

      setHistoryEditItem(null);
      setHistoryEditNote("");
      setHistoryEditConclusion("");
    } catch (error) {
      setMessage({
        type: "error",
        text:
          error.message ||
          "Historiku nuk mund të përditësohej.",
      });
    } finally {
      setHistoryEditSaving(false);
    }
  };

  const handleEditWithPermission = (repair) => {
    handleEdit(repair);
  };

  const getWarrantyConclusion = (repair) => {
  const directConclusion = String(
    repair?.technician_conclusion || ""
  ).trim();

  if (directConclusion) {
    return directConclusion;
  }

  const history = Array.isArray(repairHistory[repair?.id])
    ? repairHistory[repair.id]
    : [];

  const readyEntry = history
    .filter(
      (item) =>
        String(item?.new_status || "").toLowerCase() === "ready"
    )
    .sort(
      (a, b) =>
        new Date(b?.created_at || 0).getTime() -
        new Date(a?.created_at || 0).getTime()
    )[0];

  return String(
    readyEntry?.technician_conclusion ||
    readyEntry?.note ||
    ""
  ).trim();
};

  /* =====================================================      FORM   ===================================================== */ const handleChange =
    (event) => {
      const { name, value } = event.target;

      setFormData((previous) => {
        const next = { ...previous, [name]: value };

        // Shënimi ruhet edhe kur statusi mbetet i njëjtë,
        // sepse user-i mund të shtojë një log të ri vetëm me shënim.

        return next;
      });

      setFieldErrors((previous) => {
        if (!previous[name] && !(name === "status" && previous.statusNote)) {
          return previous;
        }

        const next = { ...previous };
        delete next[name];

        if (name === "status") {
          delete next.statusNote;
        }

        return next;
      });
    };
  const handleItemChange = (index, field, value) => {
    setFieldErrors((previous) => {
      const key = `${field}_${index}`;
      if (!previous[key]) return previous;
      const next = { ...previous };
      delete next[key];
      return next;
    });
    setFormData((previous) => ({
      ...previous,
      invoiceItems: previous.invoiceItems.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              [field]:
                field === "quantity" ? Math.max(1, Number(value || 1)) : value,
            }
          : item,
      ),
    }));
  };
  const addInvoiceItem = () => {
    setFormData((previous) => ({
      ...previous,
      invoiceItems: [...previous.invoiceItems, { ...EMPTY_ITEM }],
    }));
  };
  const removeInvoiceItem = (index) => {
    setFormData((previous) => ({
      ...previous,
      invoiceItems:
        previous.invoiceItems.length === 1
          ? [{ ...EMPTY_ITEM }]
          : previous.invoiceItems.filter((_, itemIndex) => itemIndex !== index),
    }));
  };
  const openNewRepair = () => {
    const serviceCode = generateServiceCode();
    setFormData({ ...EMPTY_FORM, serviceCode });
    setMessage({ type: "", text: "" });
    setFieldErrors({});
    setDialogOpen(true);
  };
  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setFormData(EMPTY_FORM);
    setFieldErrors({});
    setEditingOriginalStatus(null);
  };
  const handleEdit = (repair) => {
    setSelectedRepair(normalizeRepair(repair));
    setEditingOriginalStatus(repair.status || "pending");
    setFormData({
      id: repair.id,
      serviceCode: repair.service_code || "",
      name: repair.name || "",
      surname: repair.surname || "",
      phone: repair.phone || "",
      email: repair.email || "",
      phoneType: repair.phone_type || "",
      issueDescription: repair.issue_description || "",
      clientNote: repair.client_note || "",
      technicianConclusion: repair.technician_conclusion || "",
      statusNote: "",
      warrantyMonths: String(repair.warranty_months || 0),
      repairCost: String(repair.repair_cost ?? ""),
      status: repair.status || "pending",
      readyDate: repair.ready_date?.slice(0, 10) || "",
      pickupDate: repair.pickup_date?.slice(0, 10) || "",
      invoiceItems: parseInvoiceItems(repair.invoice_items),
    });
    setMessage({ type: "", text: "" });
    setFieldErrors({});
    setDialogOpen(true);
  };
  const validateForm = () => {
    const errors = {};
    const isEditing = Boolean(formData.id);
    const restrictedEdit = isEditing && !isCurrentUserAdmin;

    if (!restrictedEdit) {
      if (!formData.phone.trim())
        errors.phone = "Telefoni është i detyrueshëm.";

      if (
        formData.email.trim() &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())
      ) {
        errors.email = "Vendos një email të vlefshëm.";
      }

      if (!formData.phoneType.trim())
        errors.phoneType = "Pajisja është e detyrueshme.";

      if (!formData.issueDescription.trim()) {
        errors.issueDescription =
          "Përshkrimi i problemit është i detyrueshëm.";
      }

   // Artikulli dhe çmimi kërkohen vetëm kur servisi bëhet GATI
if (formData.status === "ready") {
  formData.invoiceItems.forEach((item, index) => {
    if (!item.article.trim()) {
      errors[`article_${index}`] =
        "Vendos artikullin ose shërbimin.";
    }

    if (Number(item.quantity) < 1) {
      errors[`quantity_${index}`] =
        "Sasia duhet të jetë të paktën 1.";
    }

    if (
      item.unitPrice === "" ||
      !Number.isFinite(Number(item.unitPrice)) ||
      Number(item.unitPrice) < 0
    ) {
      errors[`unitPrice_${index}`] =
        "Vendos një çmim të vlefshëm.";
    }
  });
}

    }

    // Kostoja e riparimit mund të ndryshohet edhe nga user-i normal.
    if (
      formData.repairCost !== "" &&
      (!Number.isFinite(Number(formData.repairCost)) ||
        Number(formData.repairCost) < 0)
    ) {
      errors.repairCost = "Kostoja e riparimit duhet të jetë 0 ose më e madhe.";
    }

    // Shënimi i specialistit është opsional.
    // Statusi dhe kostoja e riparimit mund të ruhen edhe pa shënim.

    setFieldErrors(errors);

    if (Object.keys(errors).length) {
      return "Ka fusha të paplotësuara ose të pavlefshme. Kontrollo fushat me të kuqe.";
    }

    return "";
  };

  const getWarrantyUntil = (repair) => {
  const months = Number(repair?.warranty_months || 0);

  if (months <= 0) {
    return null;
  }

  const startDate =
    repair?.pickup_date ||
    repair?.ready_date ||
    repair?.created_at;

  if (!startDate) {
    return null;
  }

  const date = new Date(startDate);

  date.setMonth(date.getMonth() + months);

  return date;
};


  /* =====================================================      SAVE   ===================================================== */ const handleSubmit =
    async (event) => {
      event.preventDefault();
      const validationError = validateForm();
      if (validationError) {
        setMessage({ type: "error", text: validationError });
        return;
      }
      const token = localStorage.getItem("authToken");
      if (!token) {
        navigate("/login");
        return;
      }
      const isEditing = Boolean(formData.id);

      const normalizedEmail = formData.email.trim().toLowerCase();

      const requestBody =
        isEditing && !isCurrentUserAdmin
          ? {
              status: formData.status,
              note: formData.statusNote.trim(),
              repairCost: Number(formData.repairCost || 0),
              repair_cost: Number(formData.repairCost || 0),
              userProfit,
            }
          : {
              serviceCode: formData.serviceCode,
              name: formData.name.trim(),
              surname: formData.surname.trim(),
              phone: formData.phone.trim(),
              email: normalizedEmail,
              phoneType: formData.phoneType.trim(),
              issueDescription: formData.issueDescription.trim(),
              clientNote: formData.clientNote.trim(),
              technicianConclusion: formData.technicianConclusion.trim(),
              note: formData.statusNote.trim(),
              servicePrice: formData.status === "ready" ? invoiceTotal : 0,
              repairCost: Number(formData.repairCost || 0),
              repair_cost: Number(formData.repairCost || 0),
              userProfit,
              warrantyMonths: Number(formData.warrantyMonths || 0),
              status: formData.status,
              readyDate: formData.readyDate || null,
              pickupDate: formData.pickupDate || null,
            invoiceItems: formData.status === "ready" ? formData.invoiceItems.map((item) => ({
                      article: item.article.trim(),
                      quantity: Number(item.quantity),
                      unitPrice: Number(item.unitPrice),
                      total:
                        Number(item.quantity) *
                        Number(item.unitPrice),
                    }))
                  : [],
            };

      const endpoint = isEditing
        ? `${API_BASE_URL}/api/repairs/${formData.id}`
        : `${API_BASE_URL}/api/repairs`;
      setSaving(true);
      setMessage({ type: "", text: "" });
      try {
        const response = await fetch(endpoint, {
          method: isEditing ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        });
        const responseText = await response.text();
        let data = {};
        try {
          data = responseText ? JSON.parse(responseText) : {};
        } catch {
          data = { error: responseText };
        }
        if (!response.ok) {
          throw new Error(
            data?.details ||
              data?.sqlMessage ||
              data?.error ||
              data?.message ||
              "Servisi nuk mund të ruhej.",
          );
        }
        const savedRepair = normalizeRepair(data);
        setRepairs((previous) =>
          isEditing
            ? previous.map((repair) =>
                Number(repair.id) === Number(savedRepair.id)
                  ? savedRepair
                  : repair,
              )
            : [savedRepair, ...previous],
        );
        setSelectedRepair(savedRepair);
        setRepairHistory((previous) => {
          const next = { ...previous };
          delete next[savedRepair.id];
          return next;
        });
        setFormData((previous) => ({
          ...previous,
          id: savedRepair.id,
          serviceCode: savedRepair.service_code || previous.serviceCode,
        }));
        setFieldErrors({});
        setMessage({
          type: "success",
          text: isEditing
            ? "Servisi u përditësua me sukses."
            : `Servisi u regjistrua me kodin ${savedRepair.service_code}.`,
        });
        setDialogOpen(false);
        setFormData(EMPTY_FORM);
        setFieldErrors({});
        setEditingOriginalStatus(null);
      } catch (error) {
        console.error("SAVE REPAIR ERROR:", error);
        setMessage({
          type: "error",
          text: error.message || "Servisi nuk mund të ruhej.",
        });
      } finally {
        setSaving(false);
      }
    };
  /* =====================================================      DELETE   ===================================================== */ const handleRemove =
    async (id) => {
      if (!window.confirm("A je e sigurt që do ta fshish këtë servis?")) {
        return;
      }
      const token = localStorage.getItem("authToken");
      if (!token) {
        navigate("/login");
        return;
      }
      try {
        const response = await fetch(`${API_BASE_URL}/api/repairs/${id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(
            data?.details ||
              data?.error ||
              data?.message ||
              "Servisi nuk mund të fshihej.",
          );
        }
        setRepairs((previous) =>
          previous.filter((repair) => Number(repair.id) !== Number(id)),
        );
        setMessage({ type: "success", text: "Servisi u fshi me sukses." });
      } catch (error) {
        setMessage({
          type: "error",
          text: error.message || "Servisi nuk mund të fshihej.",
        });
      }
    };

  /* =====================================================
       QR LABEL PDF + PRINT
    ===================================================== */

  const refreshPrinterStatus = useCallback(() => {
    try {
      const info = getPhomemoM220DeviceInfo();
      setPrinterConnected(Boolean(info?.connected));
      setPrinterName(info?.name || "Asnjë printer");
    } catch {
      setPrinterConnected(false);
      setPrinterName("Asnjë printer");
    }
  }, []);

  useEffect(() => {
    refreshPrinterStatus();

    const timer = window.setInterval(refreshPrinterStatus, 1500);
    return () => window.clearInterval(timer);
  }, [refreshPrinterStatus]);

  const handleConnectPrinter = async () => {
    if (printerConnecting) return;

    setPrinterConnecting(true);
    setMessage({ type: "", text: "" });

    try {
      const result = await connectPhomemoM220();

      if (result?.unsupported) {
        setPrinterConnected(false);
        setPrinterName("Printeri i pajisjes");

        setMessage({
          type: "success",
          text: "Ky browser nuk përdor Web Bluetooth. Printimi do të bëhet nga printeri i lidhur me pajisjen përmes dialogut standard të printimit.",
        });

        return;
      }

      setPrinterConnected(true);
      setPrinterName(result?.deviceName || "Phomemo M220");

      setMessage({
        type: "success",
        text: `Printeri ${result?.deviceName || "Phomemo M220"} u lidh me sukses.`,
      });
    } catch (error) {
      refreshPrinterStatus();

      // Mos e shfaq si gabim teknik; jep alternativën e printimit të sistemit.
      setPrinterConnected(false);
      setPrinterName("Printeri i pajisjes");

      setMessage({
        type: "success",
        text: "Lidhja direkte Bluetooth nuk u krye. Mund të vazhdosh me printimin nga printeri i lidhur me pajisjen.",
      });
    } finally {
      setPrinterConnecting(false);
    }
  };

  const createQrOnlyCanvas = async (serviceCode) => {
    const canvas = document.createElement("canvas");
    await renderQrToCanvas(canvas, serviceCode, 600);
    return canvas;
  };

  const saveBarcodeAsPdf = async (repair) => {
    if (!repair?.service_code) {
      setMessage({
        type: "error",
        text: "Ky servis nuk ka QR code.",
      });
      return;
    }

    try {
      const canvas = await createQrOnlyCanvas(repair.service_code);
      const image = canvas.toDataURL("image/png");

      // Etiketë 50x30 mm: QR katror në të djathtë + të dhënat në të majtë.
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: [50, 30],
        compress: true,
      });
  /*
      const safeName = `${repair.name || ""} ${repair.surname || ""}`.trim();
      const safeDevice = String(repair.phone_type || "");
      const safePhone = String(repair.phone || "");

      // BRAND
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(7.4);
      pdf.text("MASTERPHONE", 2.3, 4.2);

      // Vijë ndarëse nën brand
      pdf.setLineWidth(0.35);
      pdf.line(2.3, 5.3, 25.5, 5.3);

      // KLIENTI
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(7.6);
      const clientName = pdf.splitTextToSize(safeName || "Klient", 22.5)[0];
      pdf.text(clientName, 2.3, 8.8);

      // PAJISJA + TELEFONI
      pdf.setFontSize(5.7);
      pdf.text("Pajisja:", 2.3, 12.3);
      pdf.setFont("helvetica", "normal");
      const deviceLine = pdf.splitTextToSize(safeDevice || "—", 14.5)[0];
      pdf.text(deviceLine, 9.3, 12.3);

      pdf.setFont("helvetica", "bold");
      pdf.text("Tel:", 2.3, 15.6);
      pdf.setFont("helvetica", "normal");
      const phoneLine = pdf.splitTextToSize(safePhone || "—", 17)[0];
      pdf.text(phoneLine, 6.1, 15.6);
*/
      // SERVICE CODE - stil si etiketa në preview:
      // kutia e zezë "KODI I SERVISIT" + kodi në një rresht.
      const serviceBoxX = 2.3;
      const serviceBoxY = 19.0;
      const serviceBoxH = 5.2;
      const labelBoxW = 9.2;
      const codeBoxW = 14.0;

      pdf.setFillColor(0, 0, 0);
      pdf.setDrawColor(0, 0, 0);
      pdf.roundedRect(
        serviceBoxX,
        serviceBoxY,
        labelBoxW + codeBoxW,
        serviceBoxH,
        0.8,
        0.8,
        "S",
      );

      pdf.rect(serviceBoxX, serviceBoxY, labelBoxW, serviceBoxH, "F");

      pdf.setTextColor(255, 255, 255);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(3.05);
      pdf.text(
        "KODI I SERVISIT",
        serviceBoxX + labelBoxW / 2,
        serviceBoxY + 3.25,
        { align: "center" },
      );

      pdf.setTextColor(0, 0, 0);
      pdf.setFont("courier", "bold");

      let codeFontSize = 4.05;
      pdf.setFontSize(codeFontSize);

      const serviceCodeText = String(repair.service_code || "");

      while (
        pdf.getTextWidth(serviceCodeText) > codeBoxW - 1.2 &&
        codeFontSize > 2.7
      ) {
        codeFontSize -= 0.15;
        pdf.setFontSize(codeFontSize);
      }

      pdf.text(
        serviceCodeText,
        serviceBoxX + labelBoxW + 0.6,
        serviceBoxY + 3.3,
      );

      // QR 22 mm - i madh, katror dhe me quiet-zone të integruar.
      pdf.addImage(image, "PNG", 27.5, 4.2, 20.5, 20.5, undefined, "FAST");

      pdf.save(`MASTERPHONE-${repair.service_code}.pdf`);

      setMessage({
        type: "success",
        text: `Etiketa ${repair.service_code} u ruajt në PDF.`,
      });
    } catch (error) {
      console.error("SAVE QR PDF ERROR:", error);
      setMessage({
        type: "error",
        text: error?.message || "Etiketa PDF me QR code nuk mund të krijohej.",
      });
    }
  };

  /* =====================================================
       PRINT LABEL
       - PRINT dhe SAVE PDF përdorin të njëjtin dizajn 50x30 mm.
       - Printimi hap etiketën identike në dialogun standard.
    ===================================================== */
const printServiceBarcode = async (repair) => {
    if (!repair?.service_code) {
      setMessage({
        type: "error",
        text: "Ky servis nuk ka QR code.",
      });
      return;
    }

    setBarcodePrintingId(repair.id);
    setMessage({ type: "", text: "" });

    try {
      const result = await printServiceBarcodeM220(repair);

      if (result?.mode === "bluetooth") {
        setPrinterConnected(true);
        setPrinterName(result?.deviceName || "Phomemo M220");

        setMessage({
          type: "success",
          text: `Etiketa ${repair.service_code} u printua direkt në ${
            result?.deviceName || "Phomemo M220"
          } - 50 × 30 mm.`,
        });

        return;
      }

      if (result?.mode === "ios-share") {
        setMessage({
          type: "success",
          text: "Etiketa 50 × 30 mm u hap në Share Sheet të iPhone. Zgjidh Phomemo ose Print.",
        });

        return;
      }

      if (result?.mode === "ios-open") {
        setMessage({
          type: "success",
          text: "Etiketa 50 × 30 mm u hap në një dritare të veçantë në iPhone.",
        });

        return;
      }

      setMessage({
        type: "success",
        text: "Etiketa 50 × 30 mm u hap për printim nga sistemi.",
      });
    } catch (error) {
      console.error("BARCODE PRINT ERROR:", error);

      refreshPrinterStatus();

      setMessage({
        type: "error",
        text:
          error?.message ||
          "Etiketa 50 × 30 mm nuk mund të printohej.",
      });
    } finally {
      setBarcodePrintingId(null);
    }
  };
  const escapePrintHtml = (value) =>
    String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");

  const buildWarrantyPrintHtml = async (repair) => {
    const normalized = normalizeRepair(repair);
   const warrantyConclusion = getWarrantyConclusion(normalized);
    const items = parseInvoiceItems(normalized.invoice_items);

    const qrDataUrl = await QRCode.toDataURL(
      String(normalized.service_code || ""),
      {
        errorCorrectionLevel: "H",
        margin: 4,
        width: 700,
        color: {
          dark: "#000000",
          light: "#ffffff",
        },
      },
    );

   const itemRows = items
  .map((item, index) => {
    const quantity = Number(item.quantity || 0);
    const unitPrice = Number(item.unitPrice || 0);
    const total = quantity * unitPrice;

    return `
      <tr>
        <td class="center">${index + 1}</td>
        <td>${escapePrintHtml(item.article || "—")}</td>
        <td class="center">${quantity}</td>
        <td class="center">${formatMoney(unitPrice)} Lekë</td>
        <td class="center">${formatMoney(total)} Lekë</td>
      </tr>
    `;
  })
  .join("");

    return `<!doctype html>
<html lang="sq">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
<title>Flete Garancie Servis ${escapePrintHtml(
  `${normalized.name || ""} ${normalized.surname || ""}`.trim() || "Klient"
)}</title>
  <style>
    @page { size: A4 portrait; margin: 8mm; }

    * { box-sizing: border-box; }

    html, body {
      margin: 0;
      padding: 0;
      background: #ffffff;
      color: #000000;
      font-family: Arial, Helvetica, sans-serif;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .toolbar {
      position: sticky;
      top: 0;
      z-index: 10;
      display: flex;
      justify-content: center;
      padding: 10px;
      background: #f3f4f6;
      border-bottom: 1px solid #d1d5db;
    }

    .toolbar button {
      border: 0;
      border-radius: 10px;
      background: #2563eb;
      color: #fff;
      font-weight: 700;
      padding: 10px 18px;
      font-size: 15px;
    }

    .document {
      width: 194mm;
      max-width: 100%;
      margin: 0 auto;
      padding: 5mm;
      background: #fff;
    }

    .header {
      display: flex;
      justify-content: space-between;
      gap: 8mm;
      border-bottom: 2px solid #000;
      padding-bottom: 4mm;
    }

    .brand {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 1.5mm;
     }

    .brand > div {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
  }

  .brand h1 {
    font-size: 17pt;
    font-weight: 900;
    margin: 0 0 1mm;
  }

  .brand p {
    font-size: 8pt;
    line-height: 1.2;
    margin: 0;
  }

    .logo {
      width: 15mm;
      height: 18mm;
      object-fit: contain;
      flex-shrink: 0;
    }

    h1, h2, h3, p { margin: 0; }

    .brand h1 { font-size: 17pt; font-weight: 900; }

    .brand p, .right p {
      font-size: 8.5pt;
      line-height: 1.35;
    }

    .right { text-align: right; }
    .right h2 { font-size: 15pt; font-weight: 900; }

    .qr-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 8mm;
      margin-top: 5mm;
    }

    .qr {
      width: 30mm;
      height: 30mm;
      object-fit: contain;
    }

    .label {
      color: #6b7280;
      font-size: 8pt;
      text-transform: uppercase;
      font-weight: 700;
    }

    .code {
      margin-top: 1mm;
      font-family: "Courier New", monospace;
      font-size: 13pt;
      font-weight: 900;
      word-break: break-all;
    }

    .client-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 2.5mm 6mm;
      border-top: 1px solid #d1d5db;
      border-bottom: 1px solid #d1d5db;
      padding: 4mm 0;
      margin-top: 5mm;
      font-size: 9.5pt;
    }

    .full { grid-column: 1 / -1; }

    .section {
      margin-top: 4.5mm;
      break-inside: avoid;
      page-break-inside: avoid;
    }

    .section h3 {
      font-size: 10.5pt;
      font-weight: 900;
      margin-bottom: 2mm;
    }

    .box {
      min-height: 18mm;
      border: 1px solid #d1d5db;
      border-radius: 3mm;
      padding: 3mm;
      font-size: 9pt;
      line-height: 1.35;
      white-space: pre-wrap;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 8.5pt;
    }

    th, td {
      border: 1px solid #cbd5e1;
      padding: 2mm;
      vertical-align: middle;
    }

    th {
      background: #f3f4f6;
      font-weight: 800;
    }

    .center {
      text-align: center;
      width: 14%;
    }

    .warranty-box {
      border: 2px solid #000;
      border-radius: 3mm;
      padding: 3mm;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 5mm;
    }

    .warranty-total {
      font-size: 18px !important;
      font-weight: 900 !important;
      color: #111827 !important;
      background-color: #e5e7eb !important;
      border: 2px solid #111827 !important;
      padding: 12px !important;
      text-align: right !important;
      white-space: nowrap !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .warranty-value {
      margin-top: 1mm;
      font-size: 14pt;
      font-weight: 900;
    }

    .terms {
      font-size: 7.8pt;
      line-height: 1.45;
    }

    .terms p + p { margin-top: 1mm; }

    .stamp {
      margin: 30mm auto 0;
      width: 45%;
      text-align: center;
      border-top: 1px solid #000;
      padding-top: 2mm;
      font-size: 8.5pt;
    }

  .footer {
      margin-top: 28mm;
      border-top: 1px solid #d1d5db;
      padding-top: 2mm;
      text-align: center;
      font-size: 7pt;
      color: #6b7280;
    }

    @media print {
      .toolbar { display: none !important; }
      .document {
        width: 194mm !important;
        max-width: 194mm !important;
        margin: 0 !important;
        padding: 5mm !important;
      }
      body { overflow: visible !important; }
    }

    @media screen and (max-width: 700px) {
      .document { width: 100%; padding: 14px; }
      .header { gap: 10px; }
      .brand { gap: 8px; }
      .logo { width: 70px; }
      .brand h1 { font-size: 18px; }
      .right h2 { font-size: 16px; }
    }
  </style>
</head>
<body>
  <div class="toolbar">
    <button type="button" onclick="window.print()">Printo garancinë</button>
  </div>

  <main class="document">
    <header class="header">
      <div class="brand">
        <img class="logo" src="/logo.png" alt="MasterSol&Tech" />
        <div>
          <h1>MASTER SOL&amp;TECH</h1>
          <p><strong>Servis dhe shitje pajisjesh elektronike</strong></p>
          <p>Tel: 0688188188</p>
          <p>Adresa: Rruga e Kavajës 133, Tiranë</p>
        </div>
      </div>

      <div class="right">
        <h2>FLETË GARANCIE</h2>
        <p style="margin-top: 2mm">NIPTI: M61316023J</p>
        <p>Data: ${escapePrintHtml(
          formatDate(normalized.pickup_date || normalized.created_at),
        )}</p>
      </div>
    </header>

    <section class="qr-row">
      <div>
        <div class="label">Kodi unik i garancisë</div>
        <div class="code">${escapePrintHtml(normalized.service_code || "—")}</div>
      </div>
      <img class="qr" src="${qrDataUrl}" alt="QR Code" />
    </section>


<section
  class="client-info"
  style="
    margin-top: 22px;
    padding: 14px 16px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 12px;
    line-height: 1.7;
    background-color: #ffffff;
  "
>
  <div
    style="
      font-weight: 800;
      margin-bottom: 6px;
      font-size: 13px;
      color: #111827;
    "
  >
    Të dhënat e klientit
  </div>

  <div>
    <strong>Emri:</strong>
    ${escapePrintHtml(
      `${normalized.name || ""} ${normalized.surname || ""}`.trim() || "—"
    )}
  </div>

  <div>
    <strong>Telefoni:</strong>
    ${escapePrintHtml(normalized.phone || "—")}
  </div>

  <div>
    <strong>Email:</strong>
    ${escapePrintHtml(normalized.email || "—")}
  </div>

  <div>
    <strong>Pajisja:</strong>
    ${escapePrintHtml(normalized.phone_type || "—")}
  </div>

  <div>
    <strong>Kodi i servisit:</strong>
    ${escapePrintHtml(normalized.service_code || "—")}
  </div>
</section>

    <section class="section">
      <h3>Ndërhyrja dhe shërbimi i kryer</h3>
      <div class="box">${escapePrintHtml(warrantyConclusion || "—")}</div>
    </section>

    <section class="section">
      <h3>Artikujt dhe pjesët nën garanci</h3>
      <table>
  <thead>
    <tr>
      <th class="center">Nr.</th>
      <th>Artikulli / shërbimi</th>
      <th class="center">Sasia</th>
      <th class="center">Çmimi</th>
      <th class="center">Totali</th>
    </tr>
  </thead>

  <tbody>
    ${
      itemRows ||
      '<tr><td colspan="5" class="center">Nuk ka artikuj</td></tr>'
    }

      <td
        colspan="4"
        style="
          text-align: right;
          padding: 12px;
          font-size: 16px;
          font-weight: 900;
          background-color: #e5e7eb;
          border: 2px solid #111827;
          color: #111827;
        "
      >
        TOTALI PËR PAGESË
      </td>

      <td
        style="
          text-align: right;
          padding: 12px;
          font-size: 18px;
          font-weight: 900;
          background-color: #e5e7eb;
          border: 2px solid #111827;
          color: #111827;
          white-space: nowrap;
        "
      >
        ${formatMoney(normalized.service_price)} Lekë
      </td>
    </tr>
  </tbody>
</table>
    </section>

    <section class="section warranty-box">
      <div>
        <div class="label">Kohëzgjatja e garancisë</div>
        <div class="warranty-value">${escapePrintHtml(normalized.warranty_months || 0)} muaj</div>
      </div>
      <div>
       <div class="label">Garancia përfundon</div>
        <div class="warranty-value">
          ${escapePrintHtml(
            Number(normalized.warranty_months || 0) > 0
              ? new Date(getWarrantyUntil(normalized)).toLocaleDateString("sq-AL")
              : "—"
          )}
        </div>
    </section>

    <section class="section terms">
      <h3>Kushtet e garancisë</h3>
      <p>1. Garancia mbulon vetëm ndërhyrjen, shërbimin dhe pjesët e shënuara në këtë dokument.</p>
      <p>2. Garancia nuk mbulon thyerje, goditje, lagështi, kontakt me lëngje, djegie elektrike ose përdorim të papërshtatshëm.</p>
      <p>3. Garancia humbet nëse pajisja hapet ose riparohet nga persona të tretë.</p>
      <p>4. Klienti duhet të paraqesë këtë dokument dhe kodin unik për çdo kërkesë garancie.</p>
      <p>5. Garancia nuk mbulon probleme të ndryshme nga ndërhyrja e specifikuar në këtë dokument.</p>
    </section>

   

    <div class="footer">
      Dokument i gjeneruar elektronikisht nga MasterSol&amp;Tech.
    </div>
  </main>

  <script>
    (function () {
      var printed = false;

      function tryPrint() {
        if (printed) return;
        printed = true;

        setTimeout(function () {
          try {
            window.focus();
            window.print();
          } catch (error) {
            printed = false;
          }
        }, 350);
      }

      if (document.readyState === "complete") {
        tryPrint();
      } else {
        window.addEventListener("load", tryPrint, { once: true });
      }

      setTimeout(tryPrint, 900);
    })();
  </script>
</body>
</html>`;
  };

  const printWarrantyDocument = async (repair) => {
    if (repair.status !== "delivered") {
      setMessage({
        type: "error",
        text: "Garancia mund të printohet vetëm pasi servisi të jetë Dorëzuar.",
      });
      return;
    }

    const printWindow = window.open("", "_blank");

    if (!printWindow) {
      setMessage({
        type: "error",
        text: "Browser-i bllokoi dritaren e printimit. Lejo Pop-ups për këtë faqe dhe provo përsëri.",
      });
      return;
    }

    printWindow.document.open();
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>Duke përgatitur garancinë...</title>
          <style>
            body {
              margin: 0;
              min-height: 100vh;
              display: grid;
              place-items: center;
              font-family: Arial, sans-serif;
              background: #f8fafc;
              color: #334155;
            }
            .loading {
              text-align: center;
              padding: 24px;
            }
          </style>
        </head>
        <body>
          <div class="loading">
            <strong>Duke përgatitur fletën e garancisë...</strong>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();

    try {
      const html = await buildWarrantyPrintHtml(repair);

      if (printWindow.closed) {
        throw new Error("Dritarja e printimit u mbyll.");
      }

      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();

      setMessage({
        type: "success",
        text: "Fleta e garancisë u përgatit për printim.",
      });
    } catch (error) {
      console.error("WARRANTY PRINT ERROR:", error);

      if (!printWindow.closed) {
        printWindow.document.open();
        printWindow.document.write(`
          <!doctype html>
          <html>
            <body style="font-family:Arial,sans-serif;padding:24px">
              <h3>Printimi nuk mund të përgatitej.</h3>
              <p>${escapePrintHtml(error?.message || "Gabim gjatë krijimit të dokumentit.")}</p>
            </body>
          </html>
        `);
        printWindow.document.close();
      }

      setMessage({
        type: "error",
        text:
          error?.message ||
          "Fleta e garancisë nuk mund të përgatitej për printim.",
      });
    }
  };
  const printableItems = selectedRepair
    ? parseInvoiceItems(selectedRepair.invoice_items)
    : [];
  /* =====================================================      UI   ===================================================== */ return (
    <div className="repair-design min-h-screen overflow-x-hidden bg-slate-50 text-slate-800">
      <style>{repairDesign}</style>
      {" "}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />{" "}
      <button
        type="button"
        onClick={() => setSidebarOpen(true)}
        className="           fixed left-4 top-4 z-30           flex h-11 w-11           items-center justify-center           rounded-xl bg-blue-700           text-white shadow-lg           transition hover:bg-blue-800           active:scale-95           lg:hidden         "
        aria-label="Hap menunë"
      >
        {" "}
        <Menu size={24} />{" "}
      </button>{" "}
      <main className="           min-h-screen min-w-0 w-full           overflow-x-hidden           px-2 pb-8 pt-20           sm:px-4           lg:ml-64           lg:w-[calc(100%-16rem)]           lg:p-8         ">
        {" "}
        {/* HEADER */}{" "}
        <div className="repair-title mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          {" "}
          <div>
            {" "}
            <span className="text-xs font-bold uppercase tracking-wider text-blue-600">
              {" "}
              Menaxhimi i servisit{" "}
            </span>{" "}
            <h1 className="mt-1 text-3xl font-bold text-gray-900">
              {" "}
              Serviset{" "}
            </h1>{" "}
            <p className="mt-1 text-sm text-gray-500">
              {" "}
              Regjistro pajisjet, skano QR code dhe menaxho dokumentet e
              servisit.{" "}
            </p>{" "}
          </div>{" "}
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:justify-end">
            <div
              className={`flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-bold ${
                printerConnected
                  ? "border-green-200 bg-green-50 text-green-700"
                  : "border-gray-200 bg-white text-gray-500"
              }`}
              title={
                printerConnected
                  ? `Printer i lidhur: ${printerName}`
                  : "Printeri nuk është lidhur"
              }
            >
              <Bluetooth size={18} />
              <span className="max-w-[180px] truncate">
                {printerConnected
                  ? printerName
                  : webBluetoothSupported
                    ? "Printer Bluetooth jo i lidhur"
                    : "Printeri i pajisjes"}
              </span>
            </div>

            <button
              type="button"
              onClick={handleConnectPrinter}
              disabled={printerConnecting}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 font-semibold text-violet-700 transition hover:bg-violet-100 disabled:cursor-wait disabled:opacity-50 sm:w-auto"
            >
              <Bluetooth size={18} />
              {printerConnecting
                ? "Duke u lidhur..."
                : webBluetoothSupported
                  ? printerConnected
                    ? "Ndrysho printerin"
                    : "Lidh printerin Bluetooth"
                  : "Përdor printerin e pajisjes"}
            </button>

            <button
              type="button"
              onClick={() => {
                setScannerOpen(true);
                setScannerMode(null);
                setScannerValue("");
                setMessage({ type: "", text: "" });
              }}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-blue-200 bg-white px-5 py-3 font-semibold text-blue-700 shadow-sm transition hover:bg-blue-50 sm:w-auto"
            >
              <ScanLine size={18} />
              Skano QR code
            </button>

            <button
              type="button"
              onClick={openNewRepair}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white shadow-md transition hover:bg-blue-700 sm:w-auto"
            >
              <Plus size={18} />
              Servis i ri
            </button>
          </div>
        </div>{" "}
        {/* MESSAGE */}{" "}
        {message.text && (
          <div
            className={`               mb-5 rounded-xl               border px-4 py-3               text-sm font-medium               ${message.type === "success" ? "border-green-200 bg-green-50 text-green-700" : "border-red-200 bg-red-50 text-red-700"}             `}
          >
            {" "}
            {message.text}{" "}
          </div>
        )}{" "}
        {/* FILTERS */}{" "}
        <section className="repair-filters mb-6 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm shadow-slate-200/60 sm:p-5">
          {" "}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_220px]">
            {" "}
            <div className="relative">
              {" "}
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />{" "}
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Kërko kod, klient, telefon ose pajisje..."
                className="                   w-full rounded-xl                   border border-gray-300                   py-2.5 pl-10 pr-3                   outline-none                 "
              />{" "}
            </div>{" "}
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="rounded-xl border border-gray-300 bg-white px-3 py-2.5"
            >
              {" "}
              <option value="all"> Të gjitha statuset </option>{" "}
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {" "}
                  {option.label}{" "}
                </option>
              ))}{" "}
            </select>{" "}
          </div>{" "}
        </section>{" "}
        {/* LISTA KRYESORE - DESKTOP */}
        <section className="repair-table-panel hidden overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm md:block">
          <table className="w-full table-fixed text-[12px]">
            <thead className="bg-blue-600 text-white">
              <tr>
                <th className="px-4 py-3 text-left">Barcode</th>
                <th className="px-4 py-3 text-left">Pajisja</th>
                <th className="px-4 py-3 text-left">Emër / Mbiemër</th>
                <th className="px-4 py-3 text-left">Totali</th>
                <th className="px-4 py-3 text-left">Statusi aktual</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-4 py-12 text-center text-gray-500">
                    Duke ngarkuar...
                  </td>
                </tr>
              ) : filteredRepairs.length ? (
                filteredRepairs.map((repair) => (
                  <tr
                    key={repair.id}
                    tabIndex={0}
                    onClick={(event) => {
                      if (event.target.closest("button, a, input, select, textarea")) return;
                      openRepairDetails(repair);
                    }}
                    onKeyDown={(event) => {
                      if (event.target !== event.currentTarget) return;
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openRepairDetails(repair);
                      }
                    }}
                    className="cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 border-b border-gray-100 transition hover:bg-blue-50/50 last:border-b-0"
                  >
                    <td className="px-4 py-3">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                          <Barcode size={14} />
                        </span>
                        <span
                          className="truncate font-mono text-[10px] font-extrabold text-gray-700"
                          title={repair.service_code || `SRV-${repair.id}`}
                        >
                          {repair.service_code || `SRV-${repair.id}`}
                        </span>
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <div className="repair-device font-bold text-gray-900" title={repair.phone_type || "—"}>
                        {repair.phone_type || "—"}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        className="repair-client-link"
                        onClick={() => openRepairDetails(repair)}
                        title="Shiko detajet e servisit"
                      >
                        {repair.name} {repair.surname}
                      </button>
                      <div className="repair-client-phone">{repair.phone || "—"}</div>
                    </td>
                    <td>
                      <strong className="repair-service-total">{formatMoney(repair.service_price)} ALL</strong>
                    </td>

                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-extrabold ${getStatusClasses(repair.status)}`}
                      >
                        {getStatusLabel(repair.status)}
                      </span>
                    </td>

                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-4 py-12 text-center text-gray-500">
                    <Wrench size={36} className="mx-auto mb-2 text-gray-300" />
                    Nuk u gjet asnjë servis.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        {/* LISTA KRYESORE - MOBILE */}
        <div className="space-y-2 md:hidden">
          {loading ? (
            <div className="rounded-2xl bg-white px-4 py-10 text-center text-sm text-gray-500 shadow-sm">
              Duke ngarkuar...
            </div>
          ) : filteredRepairs.length ? (
            filteredRepairs.map((repair) => (
              <div
                key={repair.id}
                tabIndex={0}
                    onClick={(event) => {
                      if (event.target.closest("button, a, input, select, textarea")) return;
                      openRepairDetails(repair);
                    }}
                    onKeyDown={(event) => {
                      if (event.target !== event.currentTarget) return;
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openRepairDetails(repair);
                      }
                    }}
                    className="cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-1.5">
                      <Barcode size={13} className="shrink-0 text-blue-600" />
                      <span className="truncate font-mono text-[10px] font-extrabold text-gray-600">
                        {repair.service_code || `SRV-${repair.id}`}
                      </span>
                    </div>
                    <h3 className="mt-1.5 truncate text-[15px] font-extrabold text-gray-900">
                      {repair.phone_type || "—"}
                    </h3>
                    <div className="mt-1 truncate text-[12px] text-gray-500">
                      {repair.name} {repair.surname}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => openRepairDetails(repair)}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-700 active:scale-95"
                    aria-label="Shiko detajet"
                  >
                    <Eye size={17} />
                  </button>
                </div>

                <div className="mt-3 flex items-center justify-between gap-2 border-t border-gray-100 pt-2.5">
                  <span
                    className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-extrabold ${getStatusClasses(repair.status)}`}
                  >
                    {getStatusLabel(repair.status)}
                  </span>

                  <div className="text-right text-[10px] leading-4">
                    <div className="font-bold text-gray-600">
                      {formatDate(repair.created_at).split(" ")[0]}
                    </div>
                    <div className="flex items-center justify-end gap-1 font-extrabold text-blue-600">
                      <Clock3 size={10} />
                      {formatDate(repair.created_at).split(" ")[1] || "—"}
                    </div>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    disabled={barcodePrintingId === repair.id}
                    onClick={() => printServiceBarcode(repair)}
                    className="flex min-h-9 flex-1 items-center justify-center gap-1.5 rounded-xl bg-amber-50 px-3 py-2 text-[11px] font-bold text-amber-700 disabled:opacity-40"
                  >
                    <Barcode size={14} />
                    Barcode
                  </button>

                  {repair.status === "delivered" && (
                    <>


                      <button
                        type="button"
                        onClick={() => printWarrantyDocument(repair)}
                        className="flex min-h-9 flex-1 items-center justify-center gap-1.5 rounded-xl bg-violet-50 px-3 py-2 text-[11px] font-bold text-violet-700 disabled:opacity-30"
                      >
                        <ShieldCheck size={14} />
                        Garancia
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="rounded-2xl bg-white px-4 py-10 text-center text-sm text-gray-500 shadow-sm">
              <Wrench size={32} className="mx-auto mb-2 text-gray-300" />
              Nuk u gjet asnjë servis.
            </div>
          )}
        </div>

        {mobileDetailRepair && (
          <div
            className="fixed inset-0 z-[10010] flex items-end justify-center bg-black/55 backdrop-blur-sm sm:items-center sm:p-4"
            onClick={() => setMobileDetailRepair(null)}
          >
            {" "}
            <div
              className="flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:max-w-2xl sm:rounded-3xl"
              onClick={(event) => event.stopPropagation()}
            >
              {" "}
              <div className="flex items-start justify-between gap-4 border-b border-gray-100 bg-gradient-to-r from-blue-700 to-indigo-700 px-5 py-5 text-white">
                {" "}
                <div className="min-w-0">
                  {" "}
                  <span className="text-xs font-semibold uppercase tracking-wide text-blue-100">
                    Detajet e servisit
                  </span>{" "}
                  <h2 className="mt-1 truncate text-xl font-bold">
                    {" "}
                    {mobileDetailRepair.phone_type || "Pajisje pa emër"}{" "}
                  </h2>{" "}
                  <p className="mt-1 font-mono text-xs text-blue-100">
                    {" "}
                    {mobileDetailRepair.service_code ||
                      `SRV-${mobileDetailRepair.id}`}{" "}
                  </p>{" "}
                </div>{" "}
                <button
                  type="button"
                  onClick={() => setMobileDetailRepair(null)}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10"
                >
                  {" "}
                  <X size={20} />{" "}
                </button>{" "}
              </div>{" "}
              <div className="min-h-0 flex-1 overflow-y-auto p-5">
                {" "}
                <div className="grid grid-cols-2 gap-3">
                  {" "}
                  <div className="rounded-2xl bg-gray-50 p-3">
                    {" "}
                    <span className="text-xs font-medium text-gray-400">
                      Statusi
                    </span>{" "}
                    <div className="mt-2">
                      {" "}
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${getStatusClasses(mobileDetailRepair.status)}`}
                      >
                        {" "}
                        {getStatusLabel(mobileDetailRepair.status)}{" "}
                      </span>{" "}
                    </div>{" "}
                  </div>{" "}
                  <div className="rounded-2xl bg-emerald-50 p-3">
                    {" "}
                    <span className="text-xs font-medium text-gray-400">
                      Totali
                    </span>{" "}
                    <strong className="mt-2 block text-lg font-extrabold text-emerald-700">
                      {" "}
                      {formatMoney(mobileDetailRepair.service_price)} ALL{" "}
                    </strong>{" "}
                  </div>{" "}
                  <div className="rounded-2xl bg-amber-50 p-3">
                    <span className="text-xs font-medium text-gray-400">
                      Kosto riparimi
                    </span>
                    <strong className="mt-2 block text-lg font-extrabold text-amber-700">
                      {formatMoney(mobileDetailRepair.repair_cost)} ALL
                    </strong>
                  </div>
                  <div className="rounded-2xl bg-indigo-50 p-3">
                    <span className="text-xs font-medium text-gray-400">
                      Fitimi i userit
                    </span>
                    <strong className="mt-2 block text-lg font-extrabold text-indigo-700">
                      {formatMoney(Math.max(0, (Number(mobileDetailRepair.service_price || 0) - Number(mobileDetailRepair.repair_cost || 0)) * 0.5))} ALL
                    </strong>
                    <span className="mt-1 block text-[10px] font-semibold text-indigo-500">
                      {getReadyUserName(mobileDetailRepair.id)}
                    </span>
                  </div>
                  <div className="rounded-2xl bg-gray-50 p-3">
                    {" "}
                    <span className="text-xs font-medium text-gray-400">
                      Telefoni
                    </span>{" "}
                    <strong className="mt-1 block text-sm text-gray-800">
                      {mobileDetailRepair.phone || "—"}
                    </strong>{" "}
                  </div>{" "}
                  <div className="rounded-2xl bg-gray-50 p-3">
                    <span className="text-xs font-medium text-gray-400">
                      Email
                    </span>
                    <strong className="mt-1 block break-all text-sm text-gray-800">
                      {mobileDetailRepair.email || "—"}
                    </strong>
                  </div>{" "}
                  <div className="rounded-2xl bg-gray-50 p-3">
                    {" "}
                    <span className="text-xs font-medium text-gray-400">
                      Emër / Mbiemër
                    </span>{" "}
                    <strong className="mt-1 block text-sm text-gray-800">
                      {`${mobileDetailRepair.name || ""} ${mobileDetailRepair.surname || ""}`.trim() || "—"}
                    </strong>{" "}
                  </div>{" "}
                  <div className="rounded-2xl bg-gray-50 p-3">
                    {" "}
                    <span className="text-xs font-medium text-gray-400">
                      Garancia
                    </span>{" "}
                    <strong className="mt-1 block text-sm text-gray-800">
                      {" "}
                      {Number(mobileDetailRepair.warranty_months || 0) > 0
                        ? `${mobileDetailRepair.warranty_months} muaj`
                        : "Pa garanci"}{" "}
                    </strong>{" "}
                  </div>{" "}
                  <div className="rounded-2xl bg-gray-50 p-3">
                    <span className="text-xs font-medium text-gray-400">Data / Ora</span>
                    <strong className="mt-1 flex items-center gap-1 text-sm text-gray-800"><Clock3 size={13} className="text-blue-600" />{formatDate(mobileDetailRepair.created_at)}</strong>
                  </div>{" "}
                  <div className="rounded-2xl bg-gray-50 p-3">
                    {" "}
                    <span className="text-xs font-medium text-gray-400">
                      Regjistruar nga
                    </span>{" "}
                    <strong className="mt-1 block text-sm text-gray-800">
                      {" "}
                      {mobileDetailRepair.created_by_name || "—"}{" "}
                    </strong>{" "}
                  </div>{" "}
                  <div className="rounded-2xl bg-gray-50 p-3">
                    <span className="text-xs font-medium text-gray-400">Gati më</span>
                    <strong className="mt-1 block text-sm text-gray-800">{formatDate(mobileDetailRepair.ready_date)}</strong>
                  </div>
                  <div className="rounded-2xl bg-gray-50 p-3">
                    <span className="text-xs font-medium text-gray-400">Dorëzuar / marrë më</span>
                    <strong className="mt-1 block text-sm text-gray-800">{formatDate(mobileDetailRepair.pickup_date)}</strong>
                  </div>
                  <div className="rounded-2xl bg-gray-50 p-3">
                    <span className="text-xs font-medium text-gray-400">Garancia deri</span>
                    <strong className="mt-1 block text-sm text-gray-800">{formatDate(mobileDetailRepair.warranty_until)}</strong>
                  </div>
                </div>{" "}
                <div className="mt-4 space-y-3">
                  {" "}
                  <div className="rounded-2xl border border-gray-200 p-4">
                    {" "}
                    <span className="text-xs font-bold uppercase tracking-wide text-gray-400">
                      Problemi
                    </span>{" "}
                    <p className="mt-2 text-sm leading-6 text-gray-700">
                      {" "}
                      {mobileDetailRepair.issue_description || "—"}{" "}
                    </p>{" "}
                  </div>{" "}
                  {mobileDetailRepair.client_note && (
                    <div className="rounded-2xl border border-amber-100 bg-amber-50/60 p-4">
                      <span className="text-xs font-bold uppercase tracking-wide text-amber-600">Shënimi i klientit</span>
                      <p className="mt-2 text-sm leading-6 text-gray-700">{mobileDetailRepair.client_note}</p>
                    </div>
                  )}
                  {mobileDetailRepair.technician_conclusion && (
                    <div className="rounded-2xl border border-blue-100 bg-blue-50/50 p-4">
                      {" "}
                      <span className="text-xs font-bold uppercase tracking-wide text-blue-500">
                        Puna e kryer
                      </span>{" "}
                      <p className="mt-2 text-sm leading-6 text-gray-700">
                        {" "}
                        {mobileDetailRepair.technician_conclusion}{" "}
                      </p>{" "}
                    </div>
                  )}{" "}
                  <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                    {/* HISTORY HEADER */}
                    <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 via-white to-blue-50/60 px-4 py-4 sm:px-5">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-sm shadow-blue-200">
                            <History size={19} />
                          </div>

                          <div>
                            <h3 className="text-sm font-extrabold text-slate-900">
                              Historiku i servisit
                            </h3>
                            <p className="mt-0.5 text-[11px] text-slate-500">
                              Çdo veprim, status dhe specialist që ka punuar mbi këtë servis.
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 self-start rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[10px] font-bold text-slate-500 shadow-sm sm:self-auto">
                          <Clock3 size={12} className="text-blue-600" />
                          {(repairHistory[mobileDetailRepair.id] || []).length} veprime
                        </div>
                      </div>
                    </div>

                    {/* HISTORY CONTENT */}
                    <div className="p-4 sm:p-5">
                      {historyLoadingId === Number(mobileDetailRepair.id) ? (
                        <div className="flex min-h-[130px] items-center justify-center">
                          <div className="text-center">
                            <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600" />
                            <p className="text-xs font-semibold text-slate-500">
                              Duke ngarkuar historikun...
                            </p>
                          </div>
                        </div>
                      ) : (repairHistory[mobileDetailRepair.id] || []).length ? (
                        <div className="relative">
                          {/* TIMELINE LINE */}
                          <div className="absolute bottom-5 left-[17px] top-5 w-px bg-slate-200 sm:left-[21px]" />

                          <div className="space-y-4">
                            {(repairHistory[mobileDetailRepair.id] || []).map(
                              (item, index, historyRows) => {
                                const isCreated =
                                  String(item?.action_type || "").toLowerCase() ===
                                  "created";

                                const isLast = index === historyRows.length - 1;

                                return (
                                  <div
                                    key={item.id || index}
                                    className="relative grid grid-cols-[36px_minmax(0,1fr)] gap-3 sm:grid-cols-[44px_minmax(0,1fr)]"
                                  >
                                    {/* TIMELINE NODE */}
                                    <div className="relative z-10 flex justify-center pt-1">
                                      <div
                                        className={`flex h-9 w-9 items-center justify-center rounded-full border-4 border-white shadow-sm ${
                                          isCreated
                                            ? "bg-emerald-500 text-white"
                                            : isLast
                                              ? "bg-blue-600 text-white"
                                              : "bg-slate-100 text-slate-500"
                                        }`}
                                      >
                                        {isCreated ? (
                                          <CheckCircle2 size={16} />
                                        ) : (
                                          <Wrench size={15} />
                                        )}
                                      </div>
                                    </div>

                                    {/* EVENT CARD */}
                                    <div
                                      className={`min-w-0 rounded-2xl border p-3.5 transition sm:p-4 ${
                                        isCreated
                                          ? "border-emerald-100 bg-emerald-50/40"
                                          : isLast
                                            ? "border-blue-100 bg-blue-50/35 shadow-sm"
                                            : "border-slate-200 bg-white"
                                      }`}
                                    >
                                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                        <div className="min-w-0">
                                          <div className="flex flex-wrap items-center gap-2">
                                            <h4
                                              className={`text-[12px] font-extrabold sm:text-[13px] ${
                                                isCreated
                                                  ? "text-emerald-800"
                                                  : "text-slate-900"
                                              }`}
                                            >
                                              {getHistoryActionLabel(item)}
                                            </h4>

                                            {isCreated && (
                                              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-emerald-700">
                                                Regjistrimi fillestar
                                              </span>
                                            )}

                                            {isLast && !isCreated && (
                                              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-blue-700">
                                                Veprimi i fundit
                                              </span>
                                            )}
                                          </div>

                                          {isCurrentUserAdmin && !item.virtual && (
                                            <button
                                              type="button"
                                              onClick={() => openHistoryEdit(item)}
                                              className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-white px-2.5 py-1.5 text-[10px] font-bold text-blue-700 transition hover:bg-blue-50"
                                            >
                                              <FaEdit />
                                              Modifiko logun
                                            </button>
                                          )}

                                          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[10px] text-slate-500 sm:text-[11px]">
                                            <span className="flex items-center gap-1.5 font-semibold">
                                              <UserRound size={12} className="text-slate-400" />
                                              {item.user_name || "Përdorues i panjohur"}
                                            </span>

                                            <span className="hidden h-1 w-1 rounded-full bg-slate-300 sm:block" />

                                            <span className="flex items-center gap-1.5 font-semibold">
                                              <Clock3 size={12} className="text-blue-500" />
                                              {formatDate(item.created_at)}
                                            </span>
                                          </div>
                                        </div>

                                        {/* STATUS */}
                                        <div className="shrink-0">
                                          {item.old_status ? (
                                            <div className="flex flex-wrap items-center gap-1.5">
                                              <span
                                                className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${getStatusClasses(
                                                  item.old_status,
                                                )}`}
                                              >
                                                {getStatusLabel(item.old_status)}
                                              </span>

                                              <span className="text-xs font-bold text-slate-300">
                                                →
                                              </span>

                                              <span
                                                className={`rounded-full px-2.5 py-1 text-[10px] font-extrabold ${getStatusClasses(
                                                  item.new_status,
                                                )}`}
                                              >
                                                {getStatusLabel(item.new_status)}
                                              </span>
                                            </div>
                                          ) : (
                                            <span
                                              className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-extrabold ${getStatusClasses(
                                                item.new_status,
                                              )}`}
                                            >
                                              {getStatusLabel(item.new_status)}
                                            </span>
                                          )}
                                        </div>
                                      </div>

                                      {/* SPECIALIST NOTE */}
                                      {item.note && (
                                        <div className="mt-3 rounded-xl border border-slate-200/80 bg-white/90 px-3 py-2.5">
                                          <div className="mb-1 text-[9px] font-extrabold uppercase tracking-[0.14em] text-slate-400">
                                            Shënimi i specialistit
                                          </div>
                                          <p className="whitespace-pre-wrap break-words text-[11px] leading-5 text-slate-700 sm:text-xs">
                                            {item.note}
                                          </p>
                                        </div>
                                      )}

                                      {/* TECHNICIAN CONCLUSION */}
                                      {item.technician_conclusion && (
                                        <div className="mt-2 rounded-xl border border-blue-100 bg-blue-50/70 px-3 py-2.5">
                                          <div className="mb-1 flex items-center gap-1.5 text-[9px] font-extrabold uppercase tracking-[0.14em] text-blue-500">
                                            <Wrench size={11} />
                                            Përfundimi teknik
                                          </div>
                                          <p className="whitespace-pre-wrap break-words text-[11px] leading-5 text-slate-700 sm:text-xs">
                                            {item.technician_conclusion}
                                          </p>
                                        </div>
                                      )}

                                      {!item.note &&
                                        !item.technician_conclusion &&
                                        isCreated && (
                                          <p className="mt-3 text-[11px] leading-5 text-slate-500">
                                            Servisi u regjistrua në sistem me statusin{" "}
                                            <strong className="font-bold text-slate-700">
                                              {getStatusLabel(item.new_status)}
                                            </strong>
                                            .
                                          </p>
                                        )}
                                    </div>
                                  </div>
                                );
                              },
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center">
                          <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-slate-400 shadow-sm">
                            <History size={20} />
                          </div>
                          <p className="mt-3 text-xs font-bold text-slate-600">
                            Nuk ka ende historik
                          </p>
                          <p className="mt-1 text-[10px] leading-4 text-slate-400">
                            Veprimet e ardhshme mbi këtë servis do të shfaqen këtu.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>{" "}
                  <div className="rounded-2xl border border-gray-200 p-4">
                    {" "}
                    <div className="mb-3 flex items-center justify-between">
                      {" "}
                      <span className="text-xs font-bold uppercase tracking-wide text-gray-400">
                        Artikujt / shërbimet
                      </span>{" "}
                      <strong className="text-sm text-emerald-600">
                        {" "}
                        {formatMoney(mobileDetailRepair.service_price)} ALL{" "}
                      </strong>{" "}
                    </div>{" "}
                    <div className="space-y-2">
                      {" "}
                      {parseInvoiceItems(mobileDetailRepair.invoice_items).map(
                        (item, index) => (
                          <div
                            key={index}
                            className="flex items-start justify-between gap-3 rounded-xl bg-gray-50 px-3 py-2.5"
                          >
                            {" "}
                            <div className="min-w-0">
                              {" "}
                              <p className="break-words text-sm font-semibold text-gray-800">
                                {" "}
                                {item.article || "Artikull"}{" "}
                              </p>{" "}
                              <p className="mt-0.5 text-xs text-gray-400">
                                {" "}
                                {Number(item.quantity || 0)} ×{" "}
                                {formatMoney(item.unitPrice || 0)} ALL{" "}
                              </p>{" "}
                            </div>{" "}
                            <strong className="shrink-0 text-sm text-gray-800">
                              {" "}
                              {formatMoney(
                                Number(item.quantity || 0) *
                                  Number(item.unitPrice || 0),
                              )}{" "}
                              ALL{" "}
                            </strong>{" "}
                          </div>
                        ),
                      )}{" "}
                    </div>{" "}
                  </div>{" "}
                </div>{" "}
              </div>{" "}
              <div className="grid grid-cols-2 gap-2 border-t border-gray-200 bg-white p-3">
                <button
                  type="button"
                  disabled={barcodePrintingId === mobileDetailRepair.id}
                  onClick={() => printServiceBarcode(mobileDetailRepair)}
                  className="col-span-2 flex items-center justify-center gap-2 rounded-xl bg-amber-500 px-4 py-3 text-sm font-bold text-white shadow-sm transition active:scale-[0.98] disabled:cursor-wait disabled:opacity-50"
                >
                  <Printer size={18} />
                  {barcodePrintingId === mobileDetailRepair.id ? "Duke hapur printerin..." : "Printo barcode / etiketën"}
                </button>

                <button
                  type="button"
                  onClick={() => saveBarcodeAsPdf(mobileDetailRepair)}
                  className="col-span-2 flex items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-700 transition active:scale-[0.98]"
                >
                  <Barcode size={18} /> Ruaj etiketën PDF
                </button>

                {mobileDetailRepair.status === "delivered" && (
                  <>

                    <button type="button" disabled={Number(mobileDetailRepair.warranty_months || 0) <= 0} onClick={() => printWarrantyDocument(mobileDetailRepair)} className="flex items-center justify-center gap-2 rounded-xl bg-violet-50 px-3 py-3 text-sm font-semibold text-violet-700 disabled:cursor-not-allowed disabled:opacity-30">
                      <ShieldCheck size={18} /> Garancia
                    </button>
                  </>
                )}

                <button
                  type="button"
                  onClick={() => {
                    const repair = mobileDetailRepair;
                    setMobileDetailRepair(null);
                    handleEditWithPermission(repair);
                  }}
                  className="flex items-center justify-center gap-2 rounded-xl bg-blue-50 px-3 py-3 text-sm font-semibold text-blue-700"
                >
                  <FaEdit /> Edit
                </button>

                {isCurrentUserAdmin && (
                  <button
                    type="button"
                    onClick={() => {
                      const id = mobileDetailRepair.id;
                      setMobileDetailRepair(null);
                      handleRemove(id);
                    }}
                    className="flex items-center justify-center gap-2 rounded-xl bg-red-50 px-3 py-3 text-sm font-semibold text-red-700"
                  >
                    <FaTrash /> Delete
                  </button>
                )}
              </div>{" "}
            </div>{" "}
          </div>
        )}{" "}
        {/* ADMIN - EDIT HISTORY ENTRY */}
        {historyEditItem && isCurrentUserAdmin && (
          <>
            <div
              className="fixed inset-0 z-[10020] bg-black/50 backdrop-blur-sm"
              onClick={closeHistoryEdit}
            />

            <div className="fixed left-1/2 top-1/2 z-[10021] w-[calc(100%-1rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b px-5 py-4">
                <div>
                  <h3 className="font-extrabold text-slate-900">
                    Modifiko logun
                  </h3>
                  <p className="mt-1 text-xs text-slate-500">
                    Vetëm administratori mund të korrigjojë këtë hyrje.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={closeHistoryEdit}
                  disabled={historyEditSaving}
                  className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="space-y-4 p-5">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                  <strong>{historyEditItem.user_name || "Përdorues"}</strong>
                  {" · "}
                  {formatDate(historyEditItem.created_at)}
                  <div className="mt-1 font-semibold">
                    {getStatusLabel(historyEditItem.old_status)} →{" "}
                    {getStatusLabel(historyEditItem.new_status)}
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-bold text-slate-600">
                    Shënimi i specialistit
                  </label>
                  <textarea
                    value={historyEditNote}
                    onChange={(event) => setHistoryEditNote(event.target.value)}
                    rows={4}
                    className="w-full resize-y rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    placeholder="Shënimi i logut..."
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-bold text-slate-600">
                    Përfundimi teknik
                  </label>
                  <textarea
                    value={historyEditConclusion}
                    onChange={(event) =>
                      setHistoryEditConclusion(event.target.value)
                    }
                    rows={4}
                    className="w-full resize-y rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    placeholder="Përfundimi teknik..."
                  />
                </div>

                <p className="text-[11px] leading-5 text-slate-500">
                  User-i, data/ora dhe kalimi i statusit nuk ndryshohen,
                  që audit trail të mbetet i besueshëm.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2 border-t bg-slate-50 p-4">
                <button
                  type="button"
                  onClick={saveHistoryEdit}
                  disabled={historyEditSaving}
                  className="rounded-xl bg-blue-600 px-4 py-3 text-sm font-bold text-white disabled:opacity-60"
                >
                  {historyEditSaving ? "Duke ruajtur..." : "Ruaj ndryshimin"}
                </button>

                <button
                  type="button"
                  onClick={closeHistoryEdit}
                  disabled={historyEditSaving}
                  className="rounded-xl bg-white px-4 py-3 text-sm font-bold text-slate-700 ring-1 ring-slate-200"
                >
                  Anulo
                </button>
              </div>
            </div>
          </>
        )}{" "}

        {/* FORM MODAL */}{" "}
        {dialogOpen && (
          <>
            {" "}
            <div
              className="fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm"
              onClick={closeDialog}
            />{" "}
            <div
              role="dialog"
              aria-modal="true"
              className="                 fixed left-1/2 top-1/2                 z-[9999]                 flex max-h-[88vh]                 w-[calc(100%-2rem)]                 max-w-3xl                 -translate-x-1/2                 -translate-y-1/2                 flex-col overflow-hidden                 rounded-2xl bg-white                 shadow-2xl               "
            >
              {" "}
              <div className="flex items-center justify-between border-b px-5 py-4">
                {" "}
                <h2 className="text-lg font-bold">
                  {" "}
                  {formData.id ? "Modifiko servisin" : "Regjistro servis"}{" "}
                </h2>{" "}
                <button type="button" onClick={closeDialog}>
                  {" "}
                  <X size={20} />{" "}
                </button>{" "}
              </div>{" "}
              <form
                onSubmit={handleSubmit}
                className="min-h-0 flex-1 overflow-y-auto"
              >
                {" "}
                <div className="space-y-6 p-5">
                  {" "}
                  {message.text && (
                    <div
                      className={`rounded-xl border px-4 py-3 text-sm font-semibold ${
                        message.type === "success"
                          ? "border-green-200 bg-green-50 text-green-700"
                          : "border-red-200 bg-red-50 text-red-700"
                      }`}
                    >
                      {message.text}
                    </div>
                  )}
                  <section className="rounded-2xl border border-blue-100 bg-blue-50/60 p-4">
                    {" "}
                    <div className="mb-3 flex items-center gap-2">
                      {" "}
                      <Barcode size={20} className="text-blue-600" />{" "}
                      <div>
                        {" "}
                        <h3 className="font-bold text-gray-900">
                          {" "}
                          Kodi / Barkodi i servisit{" "}
                        </h3>{" "}
                        <p className="text-xs text-gray-500">
                          {" "}
                          Gjenerohet automatikisht sapo hap formularin.{" "}
                        </p>{" "}
                      </div>{" "}
                    </div>{" "}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_330px] md:items-center">
                      {" "}
                      <div>
                        {" "}
                        <label
                          htmlFor="serviceCode"
                          className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-gray-500"
                        >
                          {" "}
                          Kodi unik{" "}
                        </label>{" "}
                        <input
                          id="serviceCode"
                          type="text"
                          value={formData.serviceCode}
                          readOnly
                          className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 font-mono text-sm font-bold text-gray-900 outline-none"
                        />{" "}
                      </div>{" "}
                      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white p-2">
                        {" "}
                        <canvas
                          ref={formBarcodeRef}
                          className="mx-auto block h-auto max-w-full"
                        />{" "}
                      </div>{" "}
                    </div>{" "}
                  </section>{" "}

                  {/* STATUSI - vendosur lart që specialisti ta shohë menjëherë gjatë editimit */}
                  <section className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-4 shadow-sm">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-blue-600">
                          Statusi i servisit
                        </p>
                        <h3 className="mt-1 text-base font-extrabold text-gray-900">
                          {formData.id ? "Përditëso fazën e punës" : "Statusi fillestar"}
                        </h3>
                      </div>

                      {formData.id && editingOriginalStatus && (
                        <span
                          className={`inline-flex rounded-full px-3 py-1.5 text-xs font-extrabold ${getStatusClasses(
                            editingOriginalStatus,
                          )}`}
                        >
                          Aktual: {getStatusLabel(editingOriginalStatus)}
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-[220px_1fr] sm:items-start">
                      <div>
                        <label className="mb-1.5 block text-xs font-bold text-gray-600">
                          {formData.id ? "Statusi i ri" : "Statusi"}
                        </label>
                        <select
                          name="status"
                          value={formData.status}
                          onChange={handleChange}
                          className="w-full rounded-xl border border-blue-200 bg-white px-3 py-3 font-bold text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        >
                          {STATUS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {formData.id ? (
                        <div>
                          <div className="mb-1.5 flex items-center justify-between gap-2">
                            <label className="text-xs font-bold text-gray-600">
                              Shënimi i specialistit
                            </label>

                            <span className="rounded-full bg-slate-100 px-2 py-1 text-[9px] font-extrabold uppercase tracking-wide text-slate-500">
                              Opsional
                            </span>
                          </div>

                          <textarea
                            name="statusNote"
                            value={formData.statusNote}
                            onChange={handleChange}
                            rows={3}
                            placeholder="Shto një shënim për këtë servis (opsionale)..."
                            className={`w-full resize-y rounded-xl border bg-white px-3 py-2.5 text-sm outline-none transition focus:ring-2 focus:ring-blue-100 ${
                              fieldErrors.statusNote
                                ? "border-red-400 bg-red-50"
                                : "border-blue-200 focus:border-blue-500"
                            }`}
                          />

                          {fieldErrors.statusNote && (
                            <p className="mt-1.5 text-xs font-semibold text-red-600">
                              {fieldErrors.statusNote}
                            </p>
                          )}

                          <p className="mt-1.5 text-[11px] leading-4 text-gray-500">
                            Çdo ruajtje shton një hyrje të re në historik. Log-et e vjetra nuk ndryshohen.
                          </p>
                        </div>
                      ) : (
                        <div className="flex min-h-[86px] items-center rounded-xl border border-dashed border-gray-300 bg-white/70 px-4 text-xs leading-5 text-gray-500">
                          Servisi i ri regjistrohet me statusin e zgjedhur.
                        </div>
                      )}
                    </div>
                  </section>{" "}

                  {isRestrictedUserEdit && (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-5 text-amber-800">
                      Si përdorues mund të ndryshosh <strong>statusin</strong>, të vendosësh
                      <strong> koston e riparimit</strong> dhe, nëse dëshiron, të shtosh një <strong>shënim</strong>.
                      Fitimi llogaritet automatikisht me 50%. Të dhënat e tjera modifikohen vetëm nga administratori.
                    </div>
                  )}

                  <fieldset
                    //disabled={isRestrictedUserEdit}
                    className={isRestrictedUserEdit ? "space-y-4 opacity-60" : "space-y-4"}
                  >
                  {/* DEVICE FIRST, THEN OPTIONAL CUSTOMER DETAILS */}
                  <section className="space-y-3 rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-gray-500">
                      Pajisja dhe të dhënat e klientit
                    </p>
                    <div>
                      <label htmlFor="repair-phone-type" className="mb-1 block text-sm font-semibold text-gray-800">
                        Pajisja <span className="text-red-600">*</span>
                      </label>
                      <input
                        id="repair-phone-type"
                        name="phoneType"
                        value={formData.phoneType}
                        onChange={handleChange}
                        placeholder="P.sh. iPhone 13 Pro"
                        required
                        className={`w-full rounded-xl border px-3 py-2.5 outline-none ${fieldErrors.phoneType ? "border-red-500 bg-red-50 focus:ring-2 focus:ring-red-100" : "border-gray-300"}`}
                      />
                      {fieldErrors.phoneType && <p className="mt-1 text-xs font-medium text-red-600">{fieldErrors.phoneType}</p>}
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <label htmlFor="repair-client-name" className="mb-1 block text-sm font-medium text-gray-700">Emri i klientit (opsional)</label>
                        <input id="repair-client-name" name="name" value={formData.name} onChange={handleChange} placeholder="Emri" className="w-full rounded-xl border border-gray-300 px-3 py-2.5 outline-none" />
                      </div>
                      <div>
                        <label htmlFor="repair-client-surname" className="mb-1 block text-sm font-medium text-gray-700">Mbiemri (opsional)</label>
                        <input id="repair-client-surname" name="surname" value={formData.surname} onChange={handleChange} placeholder="Mbiemri" className="w-full rounded-xl border border-gray-300 px-3 py-2.5 outline-none" />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="repair-client-phone" className="mb-1 block text-sm font-semibold text-gray-800">Telefoni i klientit <span className="text-red-600">*</span></label>
                      <input id="repair-client-phone" name="phone" type="tel" inputMode="tel" autoComplete="tel" required value={formData.phone} onChange={handleChange} placeholder="Numri i telefonit" className={`w-full rounded-xl border px-3 py-2.5 outline-none ${fieldErrors.phone ? "border-red-500 bg-red-50 focus:ring-2 focus:ring-red-100" : "border-gray-300"}`} />
                      {fieldErrors.phone && <p className="mt-1 text-xs font-medium text-red-600">{fieldErrors.phone}</p>}
                    </div>
                    <div>
                      <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Email i klientit (opsional)" autoComplete="email" className={`w-full rounded-xl border px-3 py-2.5 outline-none ${fieldErrors.email ? "border-red-500 bg-red-50" : "border-gray-300"}`} />
                      {fieldErrors.email && <p className="mt-1 text-xs font-medium text-red-600">{fieldErrors.email}</p>}
                    </div>
                  <div>
                    <textarea
                      name="issueDescription"
                      value={formData.issueDescription}
                      onChange={handleChange}
                      placeholder="Problemi *"
                      rows={3}
                      className={`w-full rounded-xl border px-3 py-2.5 outline-none ${
                        fieldErrors.issueDescription
                          ? "border-red-500 bg-red-50 focus:ring-2 focus:ring-red-100"
                          : "border-gray-300"
                      }`}
                    />
                    {fieldErrors.issueDescription && (
                      <p className="mt-1 text-xs font-medium text-red-600">
                        {fieldErrors.issueDescription}
                      </p>
                    )}
                  </div>{" "}
                  <textarea
                    name="clientNote"
                    value={formData.clientNote}
                    onChange={handleChange}
                    placeholder="Shënime"
                    rows={2}
                    className="w-full rounded-xl border px-3 py-2.5"
                  />{" "}
                  <textarea
                    name="technicianConclusion"
                    value={formData.technicianConclusion}
                    onChange={handleChange}
                    placeholder="Puna e kryer nga tekniku"
                    rows={3}
                    className="w-full rounded-xl border px-3 py-2.5"
                  />{" "}
                  </section>{" "}
                  {/* ITEMS */}{" "}
                  <section className="border-t pt-5">
                    {" "}
                    <div className="mb-3 flex justify-between gap-3">
                      {" "}
                      <h3 className="font-bold"> Artikujt e faturës </h3>{" "}
                      <button
                        type="button"
                        onClick={addInvoiceItem}
                        className="rounded-lg bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700"
                      >
                        {" "}
                        + Shto artikull{" "}
                      </button>{" "}
                    </div>{" "}
                    <div className="space-y-3">
                      {" "}
                      {formData.invoiceItems.map((item, index) => (
                        <div
                          key={index}
                          className="grid grid-cols-1 items-start gap-3 rounded-xl border bg-gray-50 p-3 sm:grid-cols-[1fr_100px_150px_44px]"
                        >
                          {" "}
                          <div className="min-w-0">
                            <input
                              value={item.article}
                              onChange={(event) =>
                                handleItemChange(
                                  index,
                                  "article",
                                  event.target.value,
                                )
                              }
                              placeholder="Artikulli ose shërbimi"
                              className={`w-full rounded-lg border px-3 py-2.5 outline-none ${
                                fieldErrors[`article_${index}`]
                                  ? "border-red-500 bg-red-50"
                                  : "border-gray-300"
                              }`}
                            />
                            {fieldErrors[`article_${index}`] && (
                              <p className="mt-1 text-xs font-medium text-red-600">
                                {fieldErrors[`article_${index}`]}
                              </p>
                            )}
                          </div>{" "}
                          <div className="min-w-0">
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(event) =>
                                handleItemChange(
                                  index,
                                  "quantity",
                                  event.target.value,
                                )
                              }
                              className={`w-full rounded-lg border px-3 py-2.5 outline-none ${
                                fieldErrors[`quantity_${index}`]
                                  ? "border-red-500 bg-red-50"
                                  : "border-gray-300"
                              }`}
                            />
                            {fieldErrors[`quantity_${index}`] && (
                              <p className="mt-1 text-xs font-medium text-red-600">
                                {fieldErrors[`quantity_${index}`]}
                              </p>
                            )}
                          </div>{" "}
                          <div className="min-w-0">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.unitPrice}
                              onChange={(event) =>
                                handleItemChange(
                                  index,
                                  "unitPrice",
                                  event.target.value,
                                )
                              }
                              placeholder="Çmimi ALL"
                              className={`w-full rounded-lg border px-3 py-2.5 outline-none ${
                                fieldErrors[`unitPrice_${index}`]
                                  ? "border-red-500 bg-red-50"
                                  : "border-gray-300"
                              }`}
                            />
                            {fieldErrors[`unitPrice_${index}`] && (
                              <p className="mt-1 text-xs font-medium text-red-600">
                                {fieldErrors[`unitPrice_${index}`]}
                              </p>
                            )}
                          </div>{" "}
                          <button
                            type="button"
                            onClick={() => removeInvoiceItem(index)}
                            className="flex h-11 w-11 items-center justify-center rounded-lg bg-red-50 text-red-600"
                          >
                            {" "}
                            <Trash2 size={18} />{" "}
                          </button>{" "}
                        </div>
                      ))}{" "}
                    </div>{" "}
                    <div className="mt-4 text-right text-2xl font-black">
                      {" "}
                      Totali: {formatMoney(invoiceTotal)} ALL{" "}
                    </div>{" "}
                  </section>{" "}


                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-gray-500">
                        Garancia
                      </label>
                      <select
                        name="warrantyMonths"
                        value={formData.warrantyMonths}
                        onChange={handleChange}
                        className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 outline-none"
                      >
                        {WARRANTY_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>{" "}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-gray-500">
                        Data kur bëhet gati
                      </label>
                      <input
                        type="date"
                        name="readyDate"
                        value={formData.readyDate}
                        onChange={handleChange}
                        className="w-full rounded-xl border border-gray-300 px-3 py-2.5"
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-gray-500">
                        Data e dorëzimit
                      </label>
                      <input
                        type="date"
                        name="pickupDate"
                        value={formData.pickupDate}
                        onChange={handleChange}
                        className="w-full rounded-xl border border-gray-300 px-3 py-2.5"
                      />
                    </div>
                  </div>{" "}
                  </fieldset>

                  {/* FINANCA - E MODIFIKUESHME EDHE NGA USER-I NORMAL */}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-gray-500">
                        Kosto riparimi
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        name="repairCost"
                        value={formData.repairCost}
                        onChange={handleChange}
                        placeholder="0 ALL"
                        className={`w-full rounded-xl border px-3 py-2.5 outline-none ${
                          fieldErrors.repairCost
                            ? "border-red-500 bg-red-50"
                            : "border-gray-300"
                        }`}
                      />
                      {fieldErrors.repairCost && (
                        <p className="mt-1 text-xs font-medium text-red-600">
                          {fieldErrors.repairCost}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-gray-500">
                        Fitimi i userit që e bëri gati
                      </label>
                      <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2.5">
                        <strong className="block text-lg font-extrabold text-indigo-700">
                          {formatMoney(userProfit)} ALL
                        </strong>
                        <span className="mt-1 block text-[11px] font-medium text-indigo-600">
                          50% × (Totali − Kosto riparimi)
                        </span>
                        {formData.id && (
                          <span className="mt-1 block text-[11px] font-semibold text-indigo-700">
                            Useri: {getReadyUserName(formData.id)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                </div>{" "}
                <div className="sticky bottom-0 grid grid-cols-2 gap-2 border-t bg-white p-4">
                  {" "}
                  <button
                    type="submit"
                    disabled={saving}
                    className="rounded-xl bg-blue-600 px-4 py-3 font-semibold text-white disabled:opacity-60"
                  >
                    {" "}
                    {saving ? "Duke ruajtur..." : "Ruaj"}{" "}
                  </button>{" "}
                  <button
                    type="button"
                    onClick={closeDialog}
                    disabled={saving}
                    className="rounded-xl bg-gray-100 px-4 py-3 font-semibold"
                  >
                    {" "}
                    Anulo{" "}
                  </button>{" "}
                </div>{" "}
              </form>{" "}
            </div>{" "}
          </>
        )}{" "}
        {/* =====================================================             SCANNER MODAL         ===================================================== */}{" "}
        {scannerOpen && (
          <div
            className="fixed inset-0 z-[10020] flex items-end justify-center bg-black/60 p-0 backdrop-blur-sm sm:items-center sm:p-4"
            onClick={closeScannerModal}
          >
            <div
              className="w-full max-w-xl overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-gray-100 bg-gradient-to-r from-blue-700 to-indigo-700 px-5 py-4 text-white">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-blue-100">
                    Identifikim servisi
                  </p>
                  <h2 className="mt-1 text-lg font-extrabold">Skano QR / Barcode</h2>
                </div>
                <button
                  type="button"
                  onClick={closeScannerModal}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 transition hover:bg-white/20"
                  aria-label="Mbyll scanner-in"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-4 p-5">
                {!scannerMode && (
                  <>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <button
                        type="button"
                        onClick={startCameraScanner}
                        className="flex items-center gap-3 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-left transition hover:bg-blue-100"
                      >
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white">
                          <Camera size={22} />
                        </span>
                        <span>
                          <strong className="block text-sm text-gray-900">Skano me kamerë</strong>
                          <span className="mt-0.5 block text-xs text-gray-500">Përdor kamerën e telefonit ose laptopit.</span>
                        </span>
                      </button>

                      <button
                        type="button"
                        onClick={startScreenScanner}
                        className="flex items-center gap-3 rounded-2xl border border-violet-200 bg-violet-50 p-4 text-left transition hover:bg-violet-100"
                      >
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-violet-600 text-white">
                          <Monitor size={22} />
                        </span>
                        <span>
                          <strong className="block text-sm text-gray-900">Skano nga ekrani</strong>
                          <span className="mt-0.5 block text-xs text-gray-500">Lexo QR/barcode nga një ekran i ndarë.</span>
                        </span>
                      </button>
                    </div>

                    <form onSubmit={handleManualBarcodeSubmit} className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                      <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-gray-500">
                        Kod manual / scanner USB-Bluetooth
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={scannerValue}
                          onChange={(event) => setScannerValue(event.target.value)}
                          placeholder="SRV-20260909-..."
                          autoFocus
                          className="min-w-0 flex-1 rounded-xl border border-gray-300 bg-white px-3 py-2.5 font-mono text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        />
                        <button
                          type="submit"
                          className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-slate-800"
                        >
                          Hap
                        </button>
                      </div>
                    </form>
                  </>
                )}

                {scannerMode === "camera" && (
                  <div className="space-y-3">
                  <div className="mx-auto w-full max-w-[280px] overflow-hidden rounded-2xl bg-black sm:max-w-md">
                          <video
                            ref={cameraVideoRef}
                            autoPlay
                            muted
                            playsInline
                            className="aspect-square w-full object-cover sm:aspect-video"
                          />
                        </div>
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs text-gray-500">
                        {scannerBusy ? "Drejto kamerën te QR/barcode-i..." : "Kamera është gati."}
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          stopCameraScanner();
                          setScannerMode(null);
                          setScannerBusy(false);
                        }}
                        className="rounded-xl border border-gray-200 px-3 py-2 text-xs font-bold text-gray-700 hover:bg-gray-50"
                      >
                        Kthehu
                      </button>
                    </div>
                  </div>
                )}

                {scannerMode === "screen" && (
                  <div className="space-y-3">
                    <div className="overflow-hidden rounded-2xl bg-black">
                      <video
                        ref={screenVideoRef}
                        autoPlay
                        muted
                        playsInline
                        className="aspect-video w-full object-contain"
                      />
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs text-gray-500">
                        {scannerBusy ? "Zgjidh ekranin dhe vendos QR/barcode-in në pamje..." : "Screen scanner është gati."}
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          stopScreenScanner();
                          setScannerMode(null);
                          setScannerBusy(false);
                        }}
                        className="rounded-xl border border-gray-200 px-3 py-2 text-xs font-bold text-gray-700 hover:bg-gray-50"
                      >
                        Kthehu
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}{" "}

        </main>{" "}
    </div>
  );
};
export default AdminRepairsManager;
const repairDesign = `

/* SERVISET — pamja e panelit; funksionet dhe dokumentet ruhen. */
.repair-design {
  background: #f8fafc;
  color: #1e293b;
  font-family: inherit;
}

.repair-design main {
  font-size: 14px;
  line-height: 1.5;
}

.repair-design button,
.repair-design input,
.repair-design select,
.repair-design textarea {
  font-family: inherit;
}

.repair-title {
  background: transparent;
  border: 0;
  box-shadow: none;
}

.repair-title h1 {
  font-size: 30px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: normal;
}

.repair-title p {
  font-size: 14px;
  color: #64748b;
}

.repair-title button {
  font-size: 14px;
  font-weight: 600;
  border-radius: 12px;
  box-shadow: none;
}

.repair-filters {
  padding: 20px;
  border: 1px solid #dfe6f0;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgb(15 23 42 / .04);
}

.repair-filters input,
.repair-filters select {
  min-height: 46px;
  border-color: #cbd5e1;
  border-radius: 11px;
  font-size: 14px;
  color: #475569;
}

.repair-table-panel {
  border: 1px solid #dfe6f0;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgb(15 23 42 / .04);
  overflow-x: auto;
}

.repair-table-panel table {
  min-width: 850px;
  font-size: 14px;
  border-collapse: collapse;
}

.repair-table-panel thead {
  background: #f8fafc;
  color: #64748b;
}

.repair-table-panel th {
  padding: 16px 20px;
  border-bottom: 1px solid #dfe6f0;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .4px;
}

.repair-table-panel td {
  padding: 18px 20px;
  font-size: 14px;
  color: #334155;
  vertical-align: middle;
}

.repair-table-panel tbody tr:hover {
  background: #f8fafc;
}

.repair-table-panel td .font-extrabold {
  font-weight: 600;
}

.repair-table-panel td .truncate {
  white-space: normal;
  overflow-wrap: anywhere;
  font-size: 14px;
}

.repair-table-panel td span.font-mono {
  font-family: inherit;
  font-size: 13px;
}

.repair-table-panel td .rounded-full {
  font-size: 12px;
  white-space: nowrap;
}

.repair-table-panel td button {
  border: 1px solid #e2e8f0;
  border-radius: 9px;
}

.repair-design main .font-black,
.repair-design main .font-extrabold {
  font-weight: 700;
}

.repair-design main input,
.repair-design main select,
.repair-design main textarea {
  font-size: 14px;
  border-radius: 10px;
}

.repair-design main label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: .3px;
}

.repair-design main .shadow-2xl {
  border-radius: 16px;
}

.repair-design main .bg-gradient-to-r.text-white {
  background: #f8fafc;
  background-image: none;
  color: #111827;
  border-bottom: 1px solid #e2e8f0;
}

.repair-design main .bg-gradient-to-r.text-white .text-blue-100 {
  color: #64748b;
}

.repair-design main .bg-gradient-to-r.text-white button {
  color: #64748b;
  background: #fff;
  border: 1px solid #e2e8f0;
}

.repair-design main h2 {
  font-size: 22px;
  line-height: 1.4;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.repair-design main h3 {
  font-weight: 600;
}

.repair-design button:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 3px;
}

@media (max-width: 767px) {
  .repair-title h1 {
    font-size: 26px;
  }

  .repair-filters {
    padding: 16px;
  }

  .repair-design main h2 {
    font-size: 20px;
  }
}

/* Lista më e plotë për desktop. */
.repair-table-panel table {
  width: 100%;
  min-width: 0;
  table-layout: fixed;
}
.repair-table-panel th:nth-child(1) { width: 26%; }
.repair-table-panel th:nth-child(2) { width: 24%; }
.repair-table-panel th:nth-child(3) { width: 25%; }
.repair-table-panel th:nth-child(4) { width: 12%; }
.repair-table-panel th:nth-child(5) { width: 13%; }

.repair-table-panel th,
.repair-table-panel td {
  padding-left: 16px;
  padding-right: 16px;
}

.repair-client-link {
  display: block;
  padding: 0;
  border: 0;
  background: transparent;
  color: #0f172a;
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
}

.repair-client-link:hover {
  color: #2563eb;
  text-decoration: underline;
}

.repair-client-phone {
  margin-top: 5px;
  font-size: 12px;
  color: #64748b;
}

.repair-device {
  max-width: 100%;
  font-size: 14px;
  overflow-wrap: anywhere;
  line-height: 1.5;
}

.repair-service-total {
  color: #059669;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
}
`;